import { LIBERTY_ALL_TOKENS } from './allTokens'

// Contract addresses from env.prod - each token/chain pair has a dedicated bridge contract
export const LIBERTY_SWAP_CONTRACTS: Record<string, string> = {
  // Base
  'USDC-BASE': '0xbCE3e2D0f4eC5bf4dF21743e092B18703c272e54',
  // Ethereum
  'USDC-ETH': '0x06291eeE038e94E8DEC2b3bfB6e030c0b5615506',
  'USDT-ETHEREUM': '0x12352B55e0b4305Dd83A349A5d7845bE9B5a2Eea',
  'DAI-ETHEREUM': '0xAA7a195D69327a894eeb969D3bCb89116FC78A14',
  'ETH-ETHEREUM': '0x60FDAf9198eFCD6fAF27D50E955e1A42905f2eeb',
  'PCOCK-ETHEREUM': '0x21BFA2436928a3a16232a622ed48422BC048c432',
  'WPLS-ETHEREUM': '0x05CB1107c3aa032293b6FF92A9a817158879E881',
  // pDAI moved to the V3 routers (liberty migrated it away from V1); the old
  // V1 routers are the ones the V1 ABI fits and they no longer settle bridges.
  // Verified against /v3/app/routers on 2026-09-03.
  'PDAI-ETHEREUM': '0x4DAc8619dFC77361A0752FaAbA24F5dF7A82e8F2',
  // BSC
  'USDC-BSC': '0x43f403972080406e3e6602793A5072DBc4389bAb',
  'USDT-BSC': '0xc438D51F296fF3e53d061293D2bC4Bb9fb2f7f19',
  'USD1-BSC': '0x6dD29Cc8F8a2502421900E27bBdef8378BFA3281',
  'PCOCK-BSC': '0x2c7b20e2A76AA409431d9061b846476bB0F112A7',
  // Arbitrum
  'USDC-ARBITRUM': '0x05216280d45Bb8E8dcb863186E4762090bab7b6F',
  // PulseChain
  'USDC-PULSECHAIN': '0xe7EE706a6708b691a232452c9cb267d186942F09',
  'ETH-PULSECHAIN': '0x80C2C603d72ea17A0D85B670D4489eB3012035Cd',
  'PCOCK-PULSECHAIN': '0x49Ef0CAb153954b04D5E34bd938086Acc42126F3',
  'PLS-PULSECHAIN': '0xe0ba72300C40D8e9Ad9b062E0735b41F535986d6',
  'PDAI-PULSECHAIN': '0xF5466fC719f710619566f287971123f9c2Fb03BA',
  // Solana
  'USDC-SOLANA': 'LBSarUewo4GFr8z8ekaCewLK2daJfTzV13h9GSkkSb3',
  // MegaETH (chainId 4326) is intentionally NOT supported — see
  // LIBERTY_INPUT_TOKEN_IDS / INPUT_SHIELD_TOKEN_IDS. Its router addresses are
  // omitted so `isOnchainRailgunSwapSupported` can never resolve one.
  // Polygon
  'USDC-POLYGON': '0xcb2b2a70F29a8b7467fA930A09f9271D1eF0E5A9'
}

export interface LibertyTokenInfo {
  id: string
  address: string
  /** Address used for API quote calls (native tokens use symbol like 'PLS', 'ETH') */
  quoteAddress?: string
  chainId: number
  name: string
  symbol: string
  icon: string
  decimals: number
  isNative: boolean
}

// Only tokens that have bridge contracts (bridgeable tokens)
// Mapped from TokenData.ts + env.prod
export const LIBERTY_TOKENS: LibertyTokenInfo[] = [
  // Base
  {
    id: 'ETH-BASE',
    // NATIVE_TOKEN_ADDRESS is declared further down, so the literal is used here
    // (the array is evaluated before that const is initialised).
    address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    quoteAddress: 'ETH',
    chainId: 8453,
    name: 'Ethereum',
    symbol: 'ETH',
    icon: 'eth',
    decimals: 18,
    isNative: true
  },
  // Robinhood Chain
  {
    id: 'ETH-ROBINHOOD',
    address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    quoteAddress: 'ETH',
    chainId: 4663,
    name: 'Ethereum',
    symbol: 'ETH',
    icon: 'eth',
    decimals: 18,
    isNative: true
  },
  {
    id: 'USDG-ROBINHOOD',
    address: '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168',
    chainId: 4663,
    name: 'USDG',
    symbol: 'USDG',
    icon: 'usdg',
    decimals: 6,
    isNative: false
  },
  // Monad
  {
    id: 'USDC-MONAD',
    address: '0x754704Bc059F8C67012fEd69BC8A327a5aafb603',
    chainId: 143,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDC-BASE',
    address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    chainId: 8453,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  // Ethereum
  {
    id: 'USDC-ETH',
    address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    chainId: 1,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDT-ETHEREUM',
    address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    chainId: 1,
    name: 'Tether USD',
    symbol: 'USDT',
    icon: 'usdt',
    decimals: 6,
    isNative: false
  },
  {
    id: 'DAI-ETHEREUM',
    address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
    chainId: 1,
    name: 'Dai Stablecoin',
    symbol: 'DAI',
    icon: 'dai',
    decimals: 18,
    isNative: false
  },
  {
    id: 'ETH-ETHEREUM',
    address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    quoteAddress: 'ETH',
    chainId: 1,
    name: 'Ethereum',
    symbol: 'ETH',
    icon: 'eth',
    decimals: 18,
    isNative: true
  },
  {
    id: 'PCOCK-ETHEREUM',
    address: '0x088a72024995d9858f2fbf6529d24ca9e7954bbd',
    chainId: 1,
    name: 'Peacock',
    symbol: 'PCOCK',
    icon: 'pcock',
    decimals: 18,
    isNative: false
  },
  {
    id: 'WPLS-ETHEREUM',
    address: '0xa882606494d86804b5514e07e6bd2d6a6ee6d68a',
    chainId: 1,
    name: 'Wrapped PLS',
    symbol: 'WPLS',
    icon: 'pls',
    decimals: 18,
    isNative: false
  },
  {
    id: 'PDAI-ETHEREUM',
    address: '0x12D9FE4c9494dC363c6290bFdbb2bd9ED6358C13',
    chainId: 1,
    name: 'pDAI',
    symbol: 'pDAI',
    icon: 'pdai',
    decimals: 18,
    isNative: false
  },
  // BSC
  {
    id: 'USDC-BSC',
    address: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
    chainId: 56,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 18,
    isNative: false
  },
  {
    id: 'USDT-BSC',
    address: '0x55d398326f99059ff775485246999027b3197955',
    chainId: 56,
    name: 'USDT',
    symbol: 'USDT',
    icon: 'usdt',
    decimals: 18,
    isNative: false
  },
  {
    id: 'USD1-BSC',
    address: '0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d',
    chainId: 56,
    name: 'World Liberty Financial USD',
    symbol: 'USD1',
    icon: 'usd1',
    decimals: 18,
    isNative: false
  },
  {
    id: 'PCOCK-BSC',
    address: '0x716728eba47018ae5085b96895e293f114996702',
    chainId: 56,
    name: 'Peacock',
    symbol: 'PCOCK',
    icon: 'pcock',
    decimals: 18,
    isNative: false
  },
  // Arbitrum
  {
    id: 'USDC-ARBITRUM',
    address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
    chainId: 42161,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  // PulseChain
  {
    id: 'USDC-PULSECHAIN',
    address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07',
    chainId: 369,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'ETH-PULSECHAIN',
    address: '0x02DcdD04e3F455D838cd1249292C58f3B79e3C3C',
    chainId: 369,
    name: 'Wrapped Ether from Ethereum',
    symbol: 'WETH',
    icon: 'eth',
    decimals: 18,
    isNative: false
  },
  {
    id: 'PCOCK-PULSECHAIN',
    address: '0xc10a4ed9b4042222d69ff0b374eddd47ed90fc1f',
    chainId: 369,
    name: 'Peacock',
    symbol: 'PCOCK',
    icon: 'pcock',
    decimals: 18,
    isNative: false
  },
  {
    id: 'PLS-PULSECHAIN',
    address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    quoteAddress: 'PLS',
    chainId: 369,
    name: 'Pulsechain',
    symbol: 'PLS',
    icon: 'pls',
    decimals: 18,
    isNative: true
  },
  {
    id: 'PDAI-PULSECHAIN',
    address: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
    chainId: 369,
    name: 'Dai Stablecoin',
    symbol: 'pDAI',
    icon: 'pdai',
    decimals: 18,
    isNative: false
  },
  // Polygon
  {
    id: 'USDC-POLYGON',
    address: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359',
    chainId: 137,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  }
]

/**
 * CCTP USDC tokens (Circle Cross-Chain Transfer Protocol, EVM-only).
 * Unlike LIBERTY_TOKENS these have no dedicated bridge router — burns route
 * through the shared CCTP messenger (see cctp.ts CCTP_MESSENGER_ADDRESS).
 * Kept separate so bridge-routing logic over LIBERTY_TOKENS is untouched, but
 * merged into ALL_TOKENS below for address/id lookups.
 */
export const CCTP_TOKENS: LibertyTokenInfo[] = [
  {
    id: 'USDC-OPTIMISM',
    address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
    chainId: 10,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDC-UNICHAIN',
    address: '0x078d782b760474a361dda0af3839290b0ef57ad6',
    chainId: 130,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDC-INK',
    address: '0x2D270e6886d130D724215A266106e6832161EAEd',
    chainId: 57073,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    // Circle-native USDC on Avalanche C-Chain (verified on-chain: symbol USDC,
    // 6 decimals). Usable as a burn *source* because Avalanche (43114) is
    // already a predefined wallet network, so the approve + burn can be
    // estimated and broadcast. Like OP/Unichain/Ink, the ZKX SA factory is NOT
    // deployed there (`isSAEnabled: false`), so only basic/EOA accounts can
    // actually swap from it — same trade-off the existing CCTP sources carry.
    id: 'USDC-AVALANCHE',
    address: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
    chainId: 43114,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  }
]

/**
 * CCTP *output-only* destinations for gasless USDC-PULSECHAIN swaps.
 * The relayer does the Pulse→Base→CCTP burn/mint server-side, so the wallet never
 * touches these chains on-chain — it only forwards `destChainId`/`targetToken` to the
 * relayer (verified: no provider/network lookup on the dest chainId; see
 * gasless.ts assembleGaslessSubmitRequest + docs/swap-cctp-out-plan.md §1,§3).
 * Hence NO bridge router and NO wallet network config is required; these are merged
 * into ALL_TOKENS purely so they're selectable as destinations in the to-token picker.
 * Mirrors liberty getOutputExchangeTokens('USDC-PULSECHAIN') tail. Addresses from
 * liberty TokenData.ts. (LINEA/MORPH/PLUME/WORLD/SEI, plus SONIC/HYPEREVM/CRONOS/PHAROS
 * added 2026-08-26 for Trello #445; SOLANA still skipped — needs a Solana wallet.)
 */
export const CCTP_OUTPUT_ONLY_TOKENS: LibertyTokenInfo[] = [
  {
    id: 'USDC-LINEA',
    address: '0x176211869cA2b568f2A7D4EE941E073a821EE1ff',
    chainId: 59144,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDC-MORPH',
    address: '0xCfb1186F4e93D60E60a8bDd997427D1F33bc372B',
    chainId: 2818,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDC-PLUME',
    address: '0x222365EF19F7947e5484218551B56bb3965Aa7aF',
    chainId: 98866,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    id: 'USDC-WORLD',
    address: '0x79A02482A880bCE3F13e09Da970dC34db4CD24d1',
    chainId: 480,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    // Circle-native USDC on Sei EVM (verified on-chain: symbol USDC, 6
    // decimals). Receive-only ON PURPOSE: Sei (1329) is not a wallet network —
    // it is absent from both `predefinedNetworks` and the relayer's
    // /v2/config/networks — so the wallet could never estimate or broadcast a
    // burn there. As a destination it costs nothing: the relayer mints
    // server-side and the wallet only forwards destChainId/targetToken.
    // Moving this into CCTP_TOKENS REQUIRES adding a Sei network config first.
    id: 'USDC-SEI',
    address: '0xe15fC38F6D8c56aF07bbCBe3BAf5708A2Bf42392',
    chainId: 1329,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    // Circle-native USDC on Sonic (verified on-chain 2026-08-26 via
    // rpc.soniclabs.com: eth_chainId 146, symbol USDC, 6 decimals). Address
    // taken from liberty-swap's own live bundle (TokenData 'USDC-SONIC'), not
    // guessed. Receive-only: Sonic is not a wallet network, so a burn could
    // never be estimated or broadcast here — see the USDC-SEI note above.
    id: 'USDC-SONIC',
    address: '0x29219dd400f2Bf60E5a23d13Be72B486D4038894',
    chainId: 146,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    // Circle-native USDC on HyperEVM (verified on-chain 2026-08-26 via
    // rpc.hyperliquid.xyz/evm: eth_chainId 999, symbol USDC, 6 decimals).
    // NOTE: chainid.network's public registry claims 999 is "Wanchain
    // Testnet" — a stale, conflicting entry. `chainNames.ts` carries an
    // override so the picker does not label this token as a testnet.
    id: 'USDC-HYPEREVM',
    address: '0xb88339CB7199b77E23DB6E890353E22632Ba630f',
    chainId: 999,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    // Circle-native USDC on Cronos (verified on-chain 2026-08-26 via
    // evm.cronos.org: eth_chainId 25, symbol USDC, 6 decimals).
    id: 'USDC-CRONOS',
    address: '0x3D7F2C478aAfdB65542BCB44bCeeC05849999d2D',
    chainId: 25,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  },
  {
    // Circle-native USDC on Pharos (verified on-chain 2026-08-26 via
    // rpc.pharos.xyz: eth_chainId 1672, symbol USDC, 6 decimals).
    id: 'USDC-PHAROS',
    address: '0xC879C018dB60520F4355C26eD1a6D572cdAC1815',
    chainId: 1672,
    name: 'USDC',
    symbol: 'USDC',
    icon: 'usdc',
    decimals: 6,
    isNative: false
  }
]

export const LIBERTY_SUPPORTED_CHAIN_IDS = [...new Set(LIBERTY_TOKENS.map((t) => t.chainId))]

// CCTP source chains (Optimism/Unichain/Ink) — swappable via Circle even though
// they have no LibertySwap bridge router. Kept separate from
// LIBERTY_SUPPORTED_CHAIN_IDS so router-based bridge logic is unaffected.
export const CCTP_SUPPORTED_CHAIN_IDS = [...new Set(CCTP_TOKENS.map((t) => t.chainId))]

// Native token address used by LibertySwap
export const NATIVE_TOKEN_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'

/**
 * Runtime override of bridge routers, populated from the LibertySwap
 * `/app/routers` API (see LibertySwapAPI.loadBridgeContracts). Mirrors
 * liberty-swap's dynamic ContractManager (getListBridgeContract). Null until
 * the API responds; individual tokens missing from the API still fall back to
 * the hardcoded LIBERTY_SWAP_CONTRACTS map above.
 */
let DYNAMIC_SWAP_CONTRACTS: Record<string, string> | null = null

/**
 * Contract ABI version reported by the same `/app/routers` entry, keyed by token
 * id (`"v1"` / `"v2"` / `"v3"` / …). The router ADDRESS and its ABI version must
 * always be read from the same registry response: a redeploy can change both at
 * once, and pairing a fresh address with a stale version encodes the wrong
 * selector. That is why this is set together with the addresses rather than
 * through a setter of its own.
 */
let DYNAMIC_SWAP_VERSIONS: Record<string, string> | null = null

/**
 * Set (or clear) the dynamically-loaded bridge router overrides. Pass an empty
 * map or null to clear and fall back entirely to the hardcoded constants.
 *
 * `versions` carries the `version` field of the same registry entries; it is
 * optional so existing callers (and tests) that only care about addresses keep
 * working, and omitting it clears any previously stored versions so a caller can
 * never leave this map describing a different response than the addresses do.
 */
export function setDynamicSwapContracts(
  map: Record<string, string> | null,
  versions?: Record<string, string> | null
): void {
  DYNAMIC_SWAP_CONTRACTS = map && Object.keys(map).length > 0 ? map : null
  DYNAMIC_SWAP_VERSIONS = versions && Object.keys(versions).length > 0 ? versions : null
}

/**
 * Get the bridge contract address for a given token ID.
 * Prefers the dynamically-loaded router (from the API) and falls back to the
 * hardcoded LIBERTY_SWAP_CONTRACTS when the API hasn't supplied that token.
 */
export function getSwapContractAddress(tokenId: string): string | null {
  return DYNAMIC_SWAP_CONTRACTS?.[tokenId] || LIBERTY_SWAP_CONTRACTS[tokenId] || null
}

/**
 * The router from the LIVE `/app/routers` registry only, with no fall back to the
 * hardcoded map. Callers that must not act on a stale address — the CCTP hub is
 * the burn's `mintRecipient`, so a wrong one sends funds to the wrong contract —
 * use this and fail loudly instead.
 */
export function getLoadedSwapContractAddress(tokenId: string): string | null {
  return DYNAMIC_SWAP_CONTRACTS?.[tokenId] || null
}

/**
 * Get the address to use for API quote calls.
 * Native tokens use their symbol (e.g., 'PLS', 'ETH') instead of 0xEeee... address.
 */
export function getQuoteAddress(token: LibertyTokenInfo): string {
  return token.quoteAddress || token.address
}

// Portfolio uses ZeroAddress for native tokens, LibertySwap uses 0xEeee...
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
const NATIVE_ADDRESSES = [NATIVE_TOKEN_ADDRESS.toLowerCase(), ZERO_ADDRESS.toLowerCase()]

function isNativeAddress(address: string): boolean {
  return NATIVE_ADDRESSES.includes(address.toLowerCase())
}

/**
 * Find a LibertySwap token by on-chain address and chainId.
 * Handles native token address mapping (0x000...000 <-> 0xEeee...EEeE).
 */
export function getLibertyTokenByAddressAndChain(
  address: string,
  chainId: number
): LibertyTokenInfo | null {
  const normalizedAddress = address.toLowerCase()
  const isNative = isNativeAddress(normalizedAddress)

  return (
    ALL_TOKENS.find(
      (t) =>
        t.chainId === chainId &&
        (isNative ? t.isNative : t.address.toLowerCase() === normalizedAddress)
    ) || null
  )
}

/**
 * The chain ids on which LibertySwap knows the given token address. Used to tell
 * "LibertySwap doesn't support this token at all" apart from "this token is
 * supported, but not on the chain it was asked for" (which happens when the form
 * state carries a token from a previously selected chain) so the error the user
 * sees names the real cause instead of wrongly claiming the token is unsupported.
 * Native addresses are excluded on purpose — every chain has a native token, so
 * matching them here would be meaningless.
 */
export function getLibertyTokenChainsByAddress(address: string): number[] {
  const normalizedAddress = address.toLowerCase()
  if (isNativeAddress(normalizedAddress)) return []

  return ALL_TOKENS.filter((t) => t.address.toLowerCase() === normalizedAddress).map(
    (t) => t.chainId
  )
}

/**
 * PulseChain DEX-only tokens (no bridge contract, reachable via bridge + DEX swap).
 * These are popular tokens on PulseChain tradeable via Piteas DEX.
 */
export const LIBERTY_DEX_TOKENS: LibertyTokenInfo[] = [
  {
    id: 'PLSX-PULSECHAIN',
    address: '0x95B303987A60C71504D99Aa1b13B4DA07b0790ab',
    chainId: 369,
    name: 'PulseX',
    symbol: 'PLSX',
    icon: 'plsx',
    decimals: 18,
    isNative: false
  },
  {
    id: 'HEX-PULSECHAIN',
    address: '0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39',
    chainId: 369,
    name: 'HEX',
    symbol: 'HEX',
    icon: 'hex',
    decimals: 8,
    isNative: false
  },
  {
    id: 'INC-PULSECHAIN',
    address: '0x2fa878Ab3F87CC1C9737Fc071108F904c0B0C95d',
    chainId: 369,
    name: 'Incentive',
    symbol: 'INC',
    icon: 'inc',
    decimals: 18,
    isNative: false
  },
  {
    id: 'DAI-PULSECHAIN',
    address: '0xefD766cCb38EaF1dfd701853BFCe31359239F305',
    chainId: 369,
    name: 'Dai Stablecoin',
    symbol: 'DAI',
    icon: 'dai',
    decimals: 18,
    isNative: false
  },
  {
    id: 'WPLS-PULSECHAIN',
    address: '0xA1077a294dDE1B09bB078844df40758a5D0f9a27',
    chainId: 369,
    name: 'Wrapped PLS',
    symbol: 'WPLS',
    icon: 'pls',
    decimals: 18,
    isNative: false
  },
  {
    id: 'CST-PULSECHAIN',
    address: '0x600136dA8cc6D1Ea07449514604dc4ab7098dB82',
    chainId: 369,
    name: 'Coast',
    symbol: 'CST',
    icon: 'cst',
    decimals: 6,
    isNative: false
  },
  {
    id: 'EHEX-PULSECHAIN',
    address: '0x57fde0a71132198BBeC939B98976993d8D89D225',
    chainId: 369,
    name: 'eHEX',
    symbol: 'eHEX',
    icon: 'hex',
    decimals: 8,
    isNative: false
  },
  {
    id: 'USDT-PULSECHAIN',
    address: '0x0Cb6F5a34ad42ec934882A05265A7d5F59b51A2f',
    chainId: 369,
    name: 'Tether USD',
    symbol: 'USDT',
    icon: 'usdt',
    decimals: 6,
    isNative: false
  },
{ id: 'USDL-PULSECHAIN', address: '0x0dEEd1486bc52aA0d3E6f8849cEC5adD6598A162', chainId: 369, name: 'USDL Stablecoin', symbol: 'USDL', icon: 'usdl', decimals: 18, isNative: false },
  { id: 'PXDC-PULSECHAIN', address: '0xeB6b7932Da20c6D7B3a899D5887d86dfB09A6408', chainId: 369, name: 'PXDC Stablecoin', symbol: 'PXDC', icon: 'pxdc', decimals: 18, isNative: false },
  { id: 'PWBTC-PULSECHAIN', address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', chainId: 369, name: 'Wrapped BTC', symbol: 'pWBTC', icon: 'pwbtc', decimals: 8, isNative: false },
  { id: 'BEAR-PULSECHAIN', address: '0xd6c31bA0754C4383A41c0e9DF042C62b5e918f6d', chainId: 369, name: 'TEDDY BEAR', symbol: 'BEAR', icon: 'bear', decimals: 18, isNative: false },
  { id: 'COM-PULSECHAIN', address: '0x5A9780Bfe63f3ec57f01b087cD65BD656C9034A8', chainId: 369, name: 'Communis', symbol: 'COM', icon: 'com', decimals: 12, isNative: false },
  { id: 'HDRN-PULSECHAIN', address: '0x3819f64f282bf135d62168C1e513280dAF905e06', chainId: 369, name: 'Hedron', symbol: 'HDRN', icon: 'hdrn', decimals: 9, isNative: false },
  { id: 'SOLIDX-PULSECHAIN', address: '0x8Da17Db850315A34532108f0f5458fc0401525f6', chainId: 369, name: 'Solid X', symbol: 'SOLIDX', icon: 'solidx', decimals: 18, isNative: false },
  { id: 'AXIS-PULSECHAIN', address: '0x8BDB63033b02C15f113De51EA1C3a96Af9e8ecb5', chainId: 369, name: 'AxisAlive (AXIS)', symbol: 'AXIS', icon: 'axis', decimals: 18, isNative: false },
  { id: 'ALIVE-PULSECHAIN', address: '0xB0eBAf9378d6E7531ba09403A12636947CC2f84b', chainId: 369, name: 'AxisAlive (ALIVE)', symbol: 'ALIVE', icon: 'alive', decimals: 18, isNative: false },
  { id: 'FIRE-PULSECHAIN', address: '0xf330cb1d41052dbC74D3325376Cb82E99454e501', chainId: 369, name: 'HEXFIREIO', symbol: 'FIRE', icon: 'fire', decimals: 18, isNative: false },
  { id: 'NOPE-PULSECHAIN', address: '0x8D36123903f504eB81eEB832727af517c0db26bD', chainId: 369, name: 'Nope', symbol: 'NOPE', icon: 'nope', decimals: 18, isNative: false },
  { id: 'PTS-PULSECHAIN', address: '0x2A06a971fE6ffa002fd242d437E3db2b5cC5B433', chainId: 369, name: 'Piteas', symbol: 'PTS', icon: 'pts', decimals: 18, isNative: false },
  { id: 'MOST-PULSECHAIN', address: '0xe33a5AE21F93aceC5CfC0b7b0FDBB65A0f0Be5cC', chainId: 369, name: 'MostWanted', symbol: 'MOST', icon: 'most', decimals: 18, isNative: false },
  { id: 'PUMP-PULSECHAIN', address: '0xec4252e62C6dE3D655cA9Ce3AfC12E553ebBA274', chainId: 369, name: 'PUMP.tires', symbol: 'PUMP', icon: 'pump', decimals: 18, isNative: false },
  { id: 'INTERN-PULSECHAIN', address: '0x0eb38452fdccab988838d9be74e0f00239a876e6', chainId: 369, name: 'Liberty Swap Mascot', symbol: 'INTERN', icon: 'intern', decimals: 18, isNative: false },
  { id: 'INCD-PULSECHAIN', address: '0x144Cd22AaA2a80FEd0Bb8B1DeADDc51A53Df1d50', chainId: 369, name: 'INC Dollar', symbol: 'INCD', icon: 'incd', decimals: 18, isNative: false },
  { id: 'SOL-PULSECHAIN', address: '0x873301F2B4B83FeaFF04121B68eC9231B29Ce0df', chainId: 369, name: 'Solana', symbol: 'SOL', icon: 'sol', decimals: 18, isNative: false },
  { id: 'CVRE-PULSECHAIN', address: '0x483287ded4f43552f201a103670853b5dc57d59d', chainId: 369, name: 'The CVRE Token', symbol: 'CVRE', icon: 'cvre', decimals: 18, isNative: false },
  { id: 'UNSTABLE-PULSECHAIN', address: '0x54b11a4f6536a4e843851bb54e942320a1e705be', chainId: 369, name: 'UnStable Dick Coin', symbol: 'UNSTABLE', icon: 'unstable', decimals: 18, isNative: false },
  { id: 'BRIAH-PULSECHAIN', address: '0xA80736067abDc215a3b6B66a57c6e608654d0C9a', chainId: 369, name: 'Briah', symbol: 'BRIAH', icon: 'briah', decimals: 18, isNative: false },
  { id: 'PEPE-PULSECHAIN', address: '0x1B71505D95Ab3e7234ed2239b8EC7aa65b94ae7B', chainId: 369, name: 'Pepe', symbol: 'PEPE', icon: 'pepe', decimals: 18, isNative: false },
  { id: 'DWB-PULSECHAIN', address: '0xAEbcD0F8f69ECF9587e292bdfc4d731c1abedB68', chainId: 369, name: 'dickwifbutt', symbol: 'DWB', icon: 'dwb', decimals: 18, isNative: false },
  { id: 'URMOM-PULSECHAIN', address: '0xe43b3cEE3554e120213b8B69Caf690B6C04A7ec0', chainId: 369, name: 'URMOM', symbol: 'URMOM', icon: 'urmom', decimals: 18, isNative: false },
  { id: 'JOHN-PULSECHAIN', address: '0x83a7722b431062a39154201F331344dccFa678FB', chainId: 369, name: 'MCAFEE', symbol: 'JOHN', icon: 'john', decimals: 18, isNative: false },
  { id: 'TFC-PULSECHAIN', address: '0x99F58b6979756fc129a33797eF796a6178D8A5De', chainId: 369, name: 'The Fitzgerald Company', symbol: 'TFC', icon: 'tfc', decimals: 18, isNative: false },
  { id: 'DOUBT-PULSECHAIN', address: '0x6ba0876e30CcE2A9AfC4B82D8BD8A8349DF4Ca96', chainId: 369, name: 'DOUBT', symbol: 'DOUBT', icon: 'doubt', decimals: 18, isNative: false },
  { id: 'DOGE-PULSECHAIN', address: '0xDDe9164E7E0DA7ae48b58F36B42c1c9f80e7245F', chainId: 369, name: 'Doge', symbol: 'DOGE', icon: 'doge', decimals: 18, isNative: false },
  { id: 'MEOWTH-PULSECHAIN', address: '0x7cbEd98f3679795fc8Cd8594b98bE77575eDE156', chainId: 369, name: 'Meowth ニャース', symbol: 'MEOWTH', icon: 'meowth', decimals: 18, isNative: false },
  { id: 'FRIENDSHIP-PULSECHAIN', address: '0x6A46d56F9Dd963cEAc7f60aD148d49219CC3b79f', chainId: 369, name: 'Sommi Fan Token', symbol: 'FRIENDSHIP', icon: 'friendship', decimals: 9, isNative: false },
  { id: 'NPC-PULSECHAIN', address: '0xa6C897bc61270073f998FbC8d2425f01D3aDEb65', chainId: 369, name: 'NPC', symbol: 'NPC', icon: 'npc', decimals: 18, isNative: false },
  { id: 'TOPHAT-PULSECHAIN', address: '0xc2472877F596D5052883B93777325dD7F7d11c96', chainId: 369, name: 'Top Hat', symbol: 'TOPHAT', icon: 'tophat', decimals: 18, isNative: false },
  { id: 'SPX-PULSECHAIN', address: '0x66089bFebd6d50E249fbd7F2537481A3DA2892D0', chainId: 369, name: 'SPX', symbol: 'SPX', icon: 'spx', decimals: 18, isNative: false },
  { id: 'VOX-PULSECHAIN', address: '0x290D95b17873F75B6897844844BD204173ad25AA', chainId: 369, name: 'VOX', symbol: 'VOX', icon: 'vox', decimals: 18, isNative: false },
  { id: 'GAINZ-PULSECHAIN', address: '0x62172B0Ba3C3665d50b6714B706C386D865dF0f1', chainId: 369, name: 'HEX MASCOT', symbol: 'GAINZ', icon: 'gainz', decimals: 18, isNative: false },
  { id: 'FART-PULSECHAIN', address: '0xF002Fff935c56bE365791f43Ce430929AeeC55db', chainId: 369, name: 'FART', symbol: 'FART', icon: 'fart', decimals: 18, isNative: false },
  { id: 'YEET-PULSECHAIN', address: '0x6Afd30110e9fF1b29dB3c4DAf31EF0045a6552cB', chainId: 369, name: 'YEET', symbol: 'YEET', icon: 'yeet', decimals: 18, isNative: false },
  { id: 'SEND-PULSECHAIN', address: '0xD42B6CfA7A62d54124C5d78dd631fd4606bb3825', chainId: 369, name: 'PulseSend', symbol: 'SEND', icon: 'send', decimals: 18, isNative: false },
  { id: 'PFLAN-PULSECHAIN', address: '0xBC1D0EaC2acEfB05A947c4900635701527F2B70a', chainId: 369, name: 'pFLANNY', symbol: 'PFLAN', icon: 'pflan', decimals: 18, isNative: false },
  { id: 'GASS-PULSECHAIN', address: '0x55a356151E39e240dCcB3D6e21033381bF304Cfc', chainId: 369, name: 'Gasspas', symbol: 'GASS', icon: 'gass', decimals: 18, isNative: false },
  { id: 'UP-PULSECHAIN', address: '0x131bF51e864024DF1982F2cd7B1C786e1A005152', chainId: 369, name: 'UP', symbol: 'uP', icon: 'up', decimals: 18, isNative: false },
  { id: 'UPX-PULSECHAIN', address: '0x664E58570e5835B99d281f12dD14d350315D7E2a', chainId: 369, name: 'UPX', symbol: 'uPX', icon: 'upx', decimals: 18, isNative: false },
  { id: 'CONRAD-PULSECHAIN', address: '0x2CAB428D4774a4f62FEE37C9E2101223A4995FF3', chainId: 369, name: 'CONRAD', symbol: 'CONRAD', icon: 'conrad', decimals: 18, isNative: false },
  { id: 'ATROPA-PULSECHAIN', address: '0xCc78A0acDF847A2C1714D2A925bB4477df5d48a6', chainId: 369, name: 'ATROPA', symbol: 'ATROPA', icon: 'atropa', decimals: 18, isNative: false },
  { id: 'VPLS-PULSECHAIN', address: '0x79BB3A0Ee435f957ce4f54eE8c3CFADc7278da0C', chainId: 369, name: 'Vouch Staked PLS', symbol: 'vPLS', icon: 'vpls', decimals: 18, isNative: false },
  { id: 'WICK-PULSECHAIN', address: '0x8CDaf3d630Da9E1450832924D5701CC0500E9cfC', chainId: 369, name: 'Green Wick', symbol: 'WICK', icon: 'wick', decimals: 18, isNative: false },
  { id: 'PULSEGUY-PULSECHAIN', address: '0x67922D590BA6C784f468B6B562d201113a8FbD2D', chainId: 369, name: 'Just a pulse guy', symbol: 'PULSEGUY', icon: 'pulseguy', decimals: 18, isNative: false },
  { id: 'FLY-PULSECHAIN', address: '0xeA2f5D1B310417c6fbf6F59E6674679c48f1dE65', chainId: 369, name: 'Firefly', symbol: 'Fly', icon: 'fly', decimals: 8, isNative: false },
  { id: 'HEXOS-PULSECHAIN', address: '0x39BcA0fE6Aed637042Ef19f0FdC2F918933C6b97', chainId: 369, name: 'HexO\'s', symbol: 'HexO\'s', icon: 'hexos', decimals: 18, isNative: false },
  { id: 'HELGO-PULSECHAIN', address: '0x0567CA0dE35606E9C260CC2358404B11DE21DB44', chainId: 369, name: 'HELGO', symbol: 'HELGO', icon: 'helgo', decimals: 18, isNative: false },
  { id: 'URKID-PULSECHAIN', address: '0xF2ebB3839e0687B66d6D884087874E3EBB79a9a6', chainId: 369, name: 'URKID', symbol: 'URKID', icon: 'urkid', decimals: 18, isNative: false },
  { id: 'DUMP-PULSECHAIN', address: '0x59c61B7d86B8d3F410675EA2dc897BA2667c75D5', chainId: 369, name: 'DUMP.tires', symbol: 'DUMP', icon: 'dump', decimals: 18, isNative: false },
  { id: 'TJ-PULSECHAIN', address: '0xE0E4ca1c31686755783D45260db44B9Ba0f13508', chainId: 369, name: 'Teddy Jr', symbol: 'TJ', icon: 'tj', decimals: 18, isNative: false },
  { id: 'DRAGON-PULSECHAIN', address: '0x3731031CDBaE9292A26338A6498d0b379416B819', chainId: 369, name: 'PLS Dragon', symbol: 'DRAGON', icon: 'dragon', decimals: 18, isNative: false },
  { id: 'IM-PULSECHAIN', address: '0xBBcF895BFCb57d0f457D050bb806d1499436c0CE', chainId: 369, name: 'Internet Money', symbol: 'IM', icon: 'im', decimals: 18, isNative: false },
  { id: 'TIME-PULSECHAIN', address: '0xCA35638A3fdDD02fEC597D8c1681198C06b23F58', chainId: 369, name: 'T.I.M.E. Dividend', symbol: 'TIME', icon: 'time', decimals: 18, isNative: false },
  { id: 'ISLAND-PULSECHAIN', address: '0xdfb10795e6fe7d0db68f9778ba4c575a28e8cd4c', chainId: 369, name: 'Function Island', symbol: 'ISLAND', icon: 'island', decimals: 18, isNative: false },
  { id: 'DTO-PULSECHAIN', address: '0xc438437218009EDD656d319689c902aE56b4b96F', chainId: 369, name: 'Ditto', symbol: 'DTO', icon: 'dto', decimals: 18, isNative: false },
  { id: 'SYDE-PULSECHAIN', address: '0xa2E63bD24999C5aa06089Cf69413462e0c12c774', chainId: 369, name: 'Synthesis DeFi', symbol: 'SYDE', icon: 'syde', decimals: 18, isNative: false },
  { id: 'SS-PULSECHAIN', address: '0xd3e6bf641517394a20b0d9db4134dce1c5ea8960', chainId: 369, name: 'SlingShot', symbol: 'SS', icon: 'ss', decimals: 18, isNative: false },
  { id: 'HOG-PULSECHAIN', address: '0x16757fa456F06AAB7aDa2Be7a5FF47fef96572DC', chainId: 369, name: 'HeartWifDog', symbol: 'HOG', icon: 'hog', decimals: 18, isNative: false },
  { id: 'MORT-PULSECHAIN', address: '0x37c0c4cB848Fc8AE9e7D9BceBE2Da267d3066312', chainId: 369, name: 'MORT', symbol: 'MORT', icon: 'mort', decimals: 18, isNative: false },
  { id: 'MOONX-PULSECHAIN', address: '0x1C9c8528f2a1672e0E7eaDA21167a596D126B568', chainId: 369, name: 'MOONX', symbol: 'MOONX', icon: 'moonx', decimals: 18, isNative: false },
  { id: 'BEEF-PULSECHAIN', address: '0xF0750554CBB8671a6257061A34EAaAd4a269C1b9', chainId: 369, name: 'BEEF', symbol: 'BEEF', icon: 'beef', decimals: 18, isNative: false },
  { id: 'PHEAD-PULSECHAIN', address: '0xb97aD558f852ba915E8f923e17347ae895880Fe0', chainId: 369, name: 'PHEAD', symbol: 'PHEAD', icon: 'phead', decimals: 18, isNative: false },
  { id: 'BIDET-PULSECHAIN', address: '0x4dBc433F26D5883481410605875661E30E758151', chainId: 369, name: 'BIDET', symbol: 'BIDET', icon: 'bidet', decimals: 18, isNative: false },
  { id: 'X-PULSECHAIN', address: '0xa6c4790cc7aa22ca27327cb83276f2abd687b55b', chainId: 369, name: 'X', symbol: 'X', icon: 'x', decimals: 18, isNative: false },
  { id: 'MEOW-PULSECHAIN', address: '0x9e20fe953193725ACf489c356Dc1bf1Ab089791b', chainId: 369, name: 'HEX CAT', symbol: 'MEOW', icon: 'meow', decimals: 18, isNative: false },
  { id: 'OOF-PULSECHAIN', address: '0x9b334c49821d36d435e684e7cb9b564b328126e5', chainId: 369, name: 'OpportunityOfFinance', symbol: 'OOF', icon: 'oof', decimals: 18, isNative: false },
  { id: 'CHOMP-PULSECHAIN', address: '0x7a38dfa4249b5e80cf49602e50947a47bc5edc29', chainId: 369, name: 'Chomp', symbol: 'CHOMP', icon: 'chomp', decimals: 18, isNative: false },
  { id: 'SHIB-PULSECHAIN', address: '0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE', chainId: 369, name: 'Shiba Inu', symbol: 'SHIB', icon: 'shib', decimals: 18, isNative: false },
  { id: 'ROB-PULSECHAIN', address: '0x1c2766F5949A4aA5d4cf0439067051135ffc1b28', chainId: 369, name: 'Richards Only Brothr', symbol: 'ROB', icon: 'rob', decimals: 18, isNative: false },
  { id: 'MCR369-PULSECHAIN', address: '0x7FF1C0e8C968a8Ddc1F25e3D891562EA549Ee32e', chainId: 369, name: 'Magic Carpet Ride', symbol: 'MCR369', icon: 'mcr369', decimals: 18, isNative: false },
  { id: 'ZORP-PULSECHAIN', address: '0xc46833f4931c1cd50bf724a7c5a4f67103cbdf25', chainId: 369, name: 'ZORP', symbol: 'ZORP', icon: 'zorp', decimals: 18, isNative: false },
  { id: 'PBOSS-PULSECHAIN', address: '0xA7704061372a523912B743139BcF447c23dB5e5E', chainId: 369, name: 'Ibiza Crypto Bosses', symbol: 'pBOSS', icon: 'pboss', decimals: 18, isNative: false },
  { id: 'GIGACOCK-PULSECHAIN', address: '0x14aED785b3F951Eb5aC98250E8f4f530A2F83177', chainId: 369, name: 'Giga Cock', symbol: 'GIGACOCK', icon: 'gigacock', decimals: 18, isNative: false },
  { id: 'PLSB-PULSECHAIN', address: '0x5EE84583f67D5EcEa5420dBb42b462896E7f8D06', chainId: 369, name: 'PulseBitcoin', symbol: 'PLSB', icon: 'plsb', decimals: 12, isNative: false },
  { id: 'BRIAN-PULSECHAIN', address: '0x42218E474cB63D35267E31fb15fb030A3c594d7D', chainId: 369, name: 'Brian', symbol: 'BRIAN', icon: 'brian', decimals: 18, isNative: false },
  { id: 'DBP-PULSECHAIN', address: '0x9DDeA7d549B979Ca511788Be494EDC66AB8111B3', chainId: 369, name: 'Direct Buy Pressure', symbol: 'DBP', icon: 'dbp', decimals: 18, isNative: false },
  { id: 'FM-PULSECHAIN', address: '0x60Dd286B42f704aB9B89bf34D53DF39Fb89150E6', chainId: 369, name: 'FingerMonkey', symbol: 'FM', icon: 'fm', decimals: 18, isNative: false },
  { id: 'PDONG-PULSECHAIN', address: '0x47C7Ae1514Ee90c43e2bD50CAa8DA63285ae146d', chainId: 369, name: 'pDONG', symbol: 'pDONG', icon: 'pdong', decimals: 18, isNative: false },
  { id: 'WILLOW-PULSECHAIN', address: '0xB2b7F427A71D62DF6be55630333A9D9fced2BAA8', chainId: 369, name: 'Willow the Dog', symbol: 'WILLOW', icon: 'willow', decimals: 18, isNative: false },
  { id: 'STOMP-PULSECHAIN', address: '0x6458c4ce85b47fb2d219f53bf015e3b0dc752dd4', chainId: 369, name: 'Pulse Stomp', symbol: 'STOMP', icon: 'stomp', decimals: 18, isNative: false },
  { id: 'BELLA-PULSECHAIN', address: '0x895c1F5F4396a2a9D3DC778F63d9ddd58de8286f', chainId: 369, name: 'Bella the Dog', symbol: 'BELLA', icon: 'bella', decimals: 18, isNative: false },
  { id: 'MINICOCK-PULSECHAIN', address: '0xF3e5790df5B54E0F709A2378ac6586a52F2C2239', chainId: 369, name: 'Mini Cock', symbol: 'MINICOCK', icon: 'minicock', decimals: 18, isNative: false },
  { id: 'PTRAIN-PULSECHAIN', address: '0xCc45eBA8DBE56E932C792DD94c3Ee29b3bF05D53', chainId: 369, name: 'PulseChain Train', symbol: 'PTRAIN', icon: 'ptrain', decimals: 18, isNative: false },
  { id: 'BAANA-PULSECHAIN', address: '0x637Ecd6b33BD8d5A550939A2e6058Dd7Dc52812e', chainId: 369, name: 'Baaana Massik', symbol: 'BAANA', icon: 'baana', decimals: 18, isNative: false },
  { id: 'STM-PULSECHAIN', address: '0x62bd78d40A9FCb4D29F6fF183CFbcaf2f5ca9B52', chainId: 369, name: 'Surgicaltrainers.com', symbol: 'STM', icon: 'stm', decimals: 18, isNative: false },
  { id: 'BKD-PULSECHAIN', address: '0xA9714fF503f673E1F093A6b8A6Df7A2156B8B34C', chainId: 369, name: 'BAKED', symbol: 'BKD', icon: 'bkd', decimals: 18, isNative: false },
  { id: 'GT-PULSECHAIN', address: '0xA99DF47d9fA5dCbe69211787faE6780bC4172492', chainId: 369, name: 'Pulse Golden Ticket', symbol: 'GT', icon: 'gt', decimals: 18, isNative: false },
  { id: 'PCOIN-PULSECHAIN', address: '0x5C912eAd44c6FC1676BA376b868e5C038549dc63', chainId: 369, name: 'pCoin', symbol: 'pCoin', icon: 'pcoin', decimals: 18, isNative: false },
  { id: 'ERECTION-PULSECHAIN', address: '0x5ca97D93D06A0B35a70FEf2F7E8fF88dD1d2DA65', chainId: 369, name: 'AMIRITE?', symbol: 'ERECTION', icon: 'erection', decimals: 18, isNative: false },
  { id: 'LBRTY-PULSECHAIN', address: '0xB261Fa283aBf9CcE0b493B50b57cb654A490f339', chainId: 369, name: 'LBRTY', symbol: 'LBRTY', icon: 'lbrty', decimals: 18, isNative: false },
  { id: 'XVR-PULSECHAIN', address: '0x5Aa590A78e974EbdF4133AFF3657E9977aF1078C', chainId: 369, name: 'Xavier University', symbol: 'XVR', icon: 'xvr', decimals: 18, isNative: false },
  { id: 'PEGGASAWASS-PULSECHAIN', address: '0x5683236720de3cf127a605E2d1037A06F033aa94', chainId: 369, name: 'Peggsawass', symbol: 'PEGGASAWASS', icon: 'peggasawass', decimals: 18, isNative: false },
  { id: '$REPOST-PULSECHAIN', address: '0xDAB22eFb4fB7c4e9B62895E0b652B9a3239Da74c', chainId: 369, name: 'repost', symbol: '$repost', icon: 'repost', decimals: 18, isNative: false },
  { id: 'GECKY-PULSECHAIN', address: '0xA2D1d9beaE78E400C990e4aecd35D7648ff83c13', chainId: 369, name: 'Gecky', symbol: 'GECKY', icon: 'gecky', decimals: 18, isNative: false },
  { id: '2PHUX-PULSECHAIN', address: '0x115f3Fa979a936167f9D208a7B7c4d85081e84BD', chainId: 369, name: '2PHUX Governance Token', symbol: '2PHUX', icon: '2phux', decimals: 18, isNative: false },
  { id: 'PRINT-PULSECHAIN', address: '0x6C203A555824ec90a215f37916cf8Db58EBe2fA3', chainId: 369, name: 'INC Printer', symbol: 'PRINT', icon: 'print', decimals: 18, isNative: false },
  { id: 'DEX-PULSECHAIN', address: '0x57953dAC106a4cDa11D90273b1B9D59E169533c0', chainId: 369, name: 'DEX', symbol: 'DEX', icon: 'dex', decimals: 18, isNative: false },
  { id: 'XAVIER-PULSECHAIN', address: '0x79e8AE8F8a19280354dbb9ee844ED80CE9a8219d', chainId: 369, name: 'President Of PulseChain', symbol: 'XAVIER', icon: 'xavier', decimals: 18, isNative: false },
  { id: 'PLAND-PULSECHAIN', address: '0xEE126a8903b1318f2a2Eb739Fd80481da6E028D3', chainId: 369, name: 'PulseLand', symbol: 'PLAND', icon: 'pland', decimals: 18, isNative: false },
  { id: 'CHARIZARD-PULSECHAIN', address: '0xDa7B9b34ed19f7d26374515CA38a268f6C880B9F', chainId: 369, name: 'Charizard リザードン', symbol: 'Charizard', icon: 'charizard', decimals: 18, isNative: false },
  { id: 'YEP-PULSECHAIN', address: '0xE08FC6Ce880D36a1167701028c0ae84dc3e82b8f', chainId: 369, name: 'YEP', symbol: 'YEP', icon: 'yep', decimals: 18, isNative: false },
  { id: 'FED-PULSECHAIN', address: '0x1D177CB9EfEEa49A8B97ab1C72785a3A37ABc9Ff', chainId: 369, name: 'Reserve Teh', symbol: 'FED', icon: 'fed', decimals: 18, isNative: false },
  { id: 'TBILL-PULSECHAIN', address: '0x463413c579D29c26D59a65312657DFCe30D545A1', chainId: 369, name: 'TREASURY BILL', symbol: 'TBILL', icon: 'tbill', decimals: 18, isNative: false },
  { id: 'BFF-PULSECHAIN', address: '0xE35A842eb1EDca4C710B6c1B1565cE7df13f5996', chainId: 369, name: '𑀭𑀭𑀭𑀭𑀭', symbol: 'BFF', icon: 'bff', decimals: 18, isNative: false },
  { id: 'GBABY-PULSECHAIN', address: '0x9DC72e4AD4d11841993F6c0a087F5B9fb458aA7C', chainId: 369, name: 'In The Know ', symbol: 'GBABY', icon: 'gbaby', decimals: 18, isNative: false },
  { id: 'AFFECTION-PULSECHAIN', address: '0x24F0154C1dCe548AdF15da2098Fdd8B8A3B8151D', chainId: 369, name: 'AFFECTION™', symbol: 'AFFECTION', icon: 'affection', decimals: 18, isNative: false },
  { id: 'PEGG-PULSECHAIN', address: '0x2bD0dD9935E95c32B0c8D18B308D5ccA10Ea50F3', chainId: 369, name: 'PEG EGG', symbol: 'pEGG', icon: 'pegg', decimals: 18, isNative: false },
  { id: 'PULSECHAIN-PULSECHAIN', address: '0x54E51Cfa1CF14cB03A7eF172439fEca8804dBE1D', chainId: 369, name: 'PeterParkerBushDid911KirbyPepe', symbol: 'PULSECHAIN', icon: 'pulsechain', decimals: 18, isNative: false },
  { id: 'DEVC-PULSECHAIN', address: '0xA804b9E522A2D1645a19227514CFe856Ad8C2fbC', chainId: 369, name: 'DEV Coin', symbol: 'DEVC', icon: 'devc', decimals: 18, isNative: false },
  { id: 'JAMES-PULSECHAIN', address: '0x05076bf151AC83680151111027CBfb08cA057a7A', chainId: 369, name: 'E = mc²', symbol: 'JAMES', icon: 'james', decimals: 18, isNative: false },
  { id: 'OPHIR-PULSECHAIN', address: '0xC59Be55D22CB7967ee95e5bE0770e263EE014F78', chainId: 369, name: 'OPHIR Token', symbol: 'OPHIR', icon: 'ophir', decimals: 18, isNative: false },
  { id: 'ZEN-PULSECHAIN', address: '0xebeCbffA46Eaee7CB3B3305cCE9283cf05CfD1BB', chainId: 369, name: 'ZENDAWG', symbol: 'ZEN', icon: 'zen', decimals: 18, isNative: false },
  { id: 'BOOMER-PULSECHAIN', address: '0x27b67bd4b710C352a5716b77ce1bfc2beb5CA1e2', chainId: 369, name: 'BOOMER', symbol: 'BOOMER', icon: 'boomer', decimals: 18, isNative: false },
  { id: 'BONNIE-PULSECHAIN', address: '0xeF7A713C3F10c12D8b12B7CBB78b79b92303Ccd4', chainId: 369, name: 'Richards first Car', symbol: 'BONNIE', icon: 'bonnie', decimals: 18, isNative: false },
  { id: 'PTERN-PULSECHAIN', address: '0xfB6Aa01800A2C8563F564e175Ab4D20a139D41D3', chainId: 369, name: 'Pcocks Intern', symbol: 'pTERN', icon: 'ptern', decimals: 18, isNative: false },
  { id: 'DORRA-PULSECHAIN', address: '0x393937C233D42bB965373759EcFb6265B998F17e', chainId: 369, name: 'DorraWifHat', symbol: 'DORRA', icon: 'dorra', decimals: 18, isNative: false },
  { id: 'SHILL-PULSECHAIN', address: '0xA563dA49Ca03d352EbeD583C436bed01A2aAf478', chainId: 369, name: 'Shining City', symbol: 'SHILL', icon: 'shill', decimals: 18, isNative: false },
  { id: 'ELON-PULSECHAIN', address: '0x435363A7C8C63057aAD5d9903c154b4d43E00093', chainId: 369, name: 'ELON', symbol: 'ELON', icon: 'elon', decimals: 18, isNative: false },
  { id: 'MARS-PULSECHAIN', address: '0x709e07230860FE0543DCBC359Fdf1D1b5eD13305', chainId: 369, name: 'MARS', symbol: 'MARS', icon: 'mars', decimals: 18, isNative: false },
  { id: 'TRUMP-PULSECHAIN', address: '0x8cC6d99114Edd628249fAbc8a4d64F9A759a77Bf', chainId: 369, name: 'TRUMP', symbol: 'Trump', icon: 'trump', decimals: 18, isNative: false },
  { id: 'PUMPBITCOIN-PULSECHAIN', address: '0xF7bf2A938f971D7e4811A1170C43d651d21A0F81', chainId: 369, name: 'PUMPBITCOIN', symbol: 'PumpBitcoin', icon: 'pumpbitcoin', decimals: 18, isNative: false },
  { id: 'QUESO-PULSECHAIN', address: '0xef6570621B4E7aD12F60ECE4FC09608769cDecD9', chainId: 369, name: 'QUESO from Burritos.Cash', symbol: 'QUESO', icon: 'queso', decimals: 18, isNative: false },
  { id: 'LWH-PULSECHAIN', address: '0x0a4Ba108b3E0E6782e1040277b9957D0719E7047', chainId: 369, name: 'LamboWifHat', symbol: 'LWH', icon: 'lwh', decimals: 18, isNative: false },
  { id: 'FLAPPY-PULSECHAIN', address: '0xCE80eDf0E34eAB96038909411B2abEbD0b1aBE1F', chainId: 369, name: 'Flappy pCock', symbol: 'FLAPPY', icon: 'flappy', decimals: 18, isNative: false },
  { id: 'TOM-PULSECHAIN', address: '0x625F49ADC6364D17D18794A2031199171b7434ef', chainId: 369, name: 'TOM LEE', symbol: 'TOM', icon: 'tom', decimals: 18, isNative: false },
  { id: 'PCLICKERDAO-PULSECHAIN', address: '0xCb3b887691CED4615ec6237EFc8b989C13dAFEf4', chainId: 369, name: 'PulseClicker.com DAO', symbol: 'pCLICKERDAO', icon: 'pclickerdao', decimals: 18, isNative: false },
  { id: 'ZENC-PULSECHAIN', address: '0x0341b46Fa57D001B27F395BbC120C420efb069BE', chainId: 369, name: 'ZENCAT', symbol: 'ZENC', icon: 'zenc', decimals: 18, isNative: false },
  { id: 'THINK-PULSECHAIN', address: '0x190837EB34bFC75482947a6661B006C6cB16eE03', chainId: 369, name: 'Think BIGGER', symbol: 'THINK', icon: 'think', decimals: 18, isNative: false },
  { id: 'BITCOINHEX-PULSECHAIN', address: '0x55C50875e890c7eE5621480baB02511C380E12C6', chainId: 369, name: 'BITCOINHEX', symbol: 'BitcoinHEX', icon: 'bitcoinhex', decimals: 18, isNative: false },
  { id: 'LOUIS-PULSECHAIN', address: '0x4045DcFbD26B90bC3db4Bb823374ADeEF28E88f7', chainId: 369, name: 'LOUIS', symbol: 'LOUIS', icon: 'louis', decimals: 18, isNative: false },
  { id: 'ZKPULSE-PULSECHAIN', address: '0x8De9077B619DcBdA28edda4b8dC16538a59EFb49', chainId: 369, name: 'ZKPULSE', symbol: 'zkPULSE', icon: 'zkpulse', decimals: 18, isNative: false },
  { id: 'DREDD-PULSECHAIN', address: '0x45462fC0b9a5eC6047484c2e572C595Df386fE61', chainId: 369, name: 'DREDD AI', symbol: 'DREDD', icon: 'dredd', decimals: 18, isNative: false },
  { id: 'PEGGY-PULSECHAIN', address: '0x2B2ED7cF7969E7529d5Ed0Aeeb639bedB9245E08', chainId: 369, name: 'PEGGY GUY', symbol: 'PEGGY', icon: 'peggy', decimals: 18, isNative: false },
  { id: 'LSD-PULSECHAIN', address: '0xe8C09c72815D01C02a6667F0A2F3f6Ce5066Fa2d', chainId: 369, name: 'Liberty Swap Dino', symbol: 'LSD', icon: 'lsd', decimals: 18, isNative: false },
  { id: 'POLLY-PULSECHAIN', address: '0xB880Ec70E9b2191D24Ac1A66a9D4e8f0533Cb711', chainId: 369, name: 'PulseChain Parrot', symbol: 'POLLY', icon: 'polly', decimals: 18, isNative: false },
  { id: 'KEEPER-PULSECHAIN', address: '0x2C08B221562c4d4db63dd3aB48761D6d2f595D06', chainId: 369, name: 'FEED THE BIRD', symbol: 'KEEPER', icon: 'keeper', decimals: 18, isNative: false },
  { id: 'RICH-PULSECHAIN', address: '0x3FE1aE9B80c95Ff14350e29DB4f3DfAC8cCC3307', chainId: 369, name: 'RICH', symbol: 'RICH', icon: 'rich', decimals: 18, isNative: false },
  { id: 'HERO-PULSECHAIN', address: '0x032F78D337F67012324901afF3bFC80487BA46b6', chainId: 369, name: 'ZERØ2HERØ', symbol: 'HERØ', icon: 'hero', decimals: 18, isNative: false },
  { id: 'ZERO-PULSECHAIN', address: '0xf6703dbff070f231eed966d33b1b6d7ef5207d26', chainId: 369, name: 'ZeroTrust', symbol: 'ZERØ', icon: 'zero', decimals: 18, isNative: false },
  { id: 'POTTER-PULSECHAIN', address: '0x5eF6d35a14b948FD8FaBE4679Ea020D91B95988b', chainId: 369, name: 'Pulse Otter AI', symbol: 'pOTTER', icon: 'potter', decimals: 18, isNative: false },
  { id: 'VINE-PULSECHAIN', address: '0xC69e2E3AC4E476e7C5565237825Ebe2e41a508C1', chainId: 369, name: 'VINE Coin', symbol: 'VINE', icon: 'vine', decimals: 18, isNative: false },
  { id: 'NOTHING-PULSECHAIN', address: '0xF33C0bB40d1BBf6EAfaAea2ADFb7d2D3EbC1E49C', chainId: 369, name: 'NOTHING', symbol: 'NOTHING', icon: 'nothing', decimals: 18, isNative: false },
  { id: 'FEE-PULSECHAIN', address: '0x0aBcDd8619Af7dC06EB74e97fFc6907bDA4B19f7', chainId: 369, name: 'COFFEE', symbol: 'FEE', icon: 'fee', decimals: 18, isNative: false },
  { id: 'COFFEE-PULSECHAIN', address: '0x707c905df6104eae3b116ed9635cbee0a9eba6e6', chainId: 369, name: 'Coffee Shop', symbol: 'COFFEE', icon: 'coffee', decimals: 18, isNative: false },
  { id: 'PV-PULSECHAIN', address: '0x4c8e8D126120C7a5B37CeAaB3165628970b731Df', chainId: 369, name: 'pVILLAGE', symbol: 'PV', icon: 'pv', decimals: 18, isNative: false },
  { id: 'BULL-PULSECHAIN', address: '0x80FB8fB13b72192515CB2b8317Eb850f16d5b085', chainId: 369, name: 'Diamond Bull', symbol: 'BULL', icon: 'bull', decimals: 18, isNative: false },
  { id: 'ZONKERS-PULSECHAIN', address: '0x9009aECE6CDb8ae862c7d9c6FB9fB801D9745179', chainId: 369, name: 'ZONKERS', symbol: 'ZONKERS', icon: 'zonkers', decimals: 18, isNative: false },
  { id: 'OGT+-PULSECHAIN', address: '0x63aa45017513B9D5bD7252F0C4752f23f77f7043', chainId: 369, name: 'Old Glory Token ELITE V6', symbol: 'OGT+', icon: 'ogt', decimals: 18, isNative: false },
  { id: 'PIKAJEW-PULSECHAIN', address: '0x36fc7d749506cAA3131Fb0c5999D2D364C59498e', chainId: 369, name: 'PIKAJEW', symbol: 'PIKAJEW', icon: 'pikajew', decimals: 18, isNative: false },
  { id: 'PLSS-PULSECHAIN', address: '0x1BC0CE24e87d998b96b42f0F8f70A5aa8d637a04', chainId: 369, name: 'Pulsican Store', symbol: 'PLSS', icon: 'plss', decimals: 18, isNative: false },
  { id: 'TEDDY-PULSECHAIN', address: '0x91Ab48C4988aE5bbEB02aCB8b5cdBCd8225D7974', chainId: 369, name: 'Teddy Bear', symbol: 'TEDDY', icon: 'teddy', decimals: 18, isNative: false },
  { id: 'LIBERTYRUG-PULSECHAIN', address: '0xd744Ed42C9e793526C47d9013AAF6CE84d0B65aE', chainId: 369, name: 'Liberty Rug', symbol: 'LIBERTYRUG', icon: 'libertyrug', decimals: 18, isNative: false },
  { id: 'SWRM-PULSECHAIN', address: '0x1E2b066d068eb087CCf85620B8306a283ea70816', chainId: 369, name: 'Swarm', symbol: 'SWRM', icon: 'swrm', decimals: 18, isNative: false },
  { id: 'STOOPID-PULSECHAIN', address: '0xfCAB7A56cB7Cb57844aF7A8816138aE6d7493A44', chainId: 369, name: 'Richard Heart\'s Stoopid', symbol: 'STOOPID', icon: 'stoopid', decimals: 18, isNative: false },
  { id: 'DEVICE-PULSECHAIN', address: '0x75A6Fca9df31F80Ffa2914836920A290f6Ab2308', chainId: 369, name: 'Free Electronic Gift', symbol: 'DEVICE', icon: 'device', decimals: 18, isNative: false },
  { id: 'BAA-PULSECHAIN', address: '0xf8B615ee7dA763b1ecd217e01f0d070a70F2107A', chainId: 369, name: 'richibaa', symbol: 'BAA', icon: 'baa', decimals: 18, isNative: false },
  { id: 'PEPEWIFHAT-PULSECHAIN', address: '0x101327d98fe1847b25a6da4b85383d9945b48c26', chainId: 369, name: 'pepewifhat', symbol: 'pepewifhat', icon: 'pepewifhat', decimals: 18, isNative: false },
  { id: 'BX-PULSECHAIN', address: '0xf6D5a563259447E8C92B69d3d65DA6bE6b02284b', chainId: 369, name: 'Black Twitter', symbol: 'BX', icon: 'bx', decimals: 18, isNative: false },
  { id: 'TABLET-PULSECHAIN', address: '0x5089912bDeb2DDCf08f396aDBd9C806059539Ddd', chainId: 369, name: 'Tablet', symbol: 'TABLET', icon: 'tablet', decimals: 18, isNative: false },
  { id: 'SIMP-PULSECHAIN', address: '0xADBD09Faf339BD18d00430Be1c2cba6c1119df1E', chainId: 369, name: 'SIMP', symbol: '$IMP', icon: 'simp', decimals: 18, isNative: false },
  { id: 'KISHKA-PULSECHAIN', address: '0xdf2eb4E30d8ca5D76C2eA9f7E246CE0a415F1db9', chainId: 369, name: 'KISHKA', symbol: 'KISHKA', icon: 'kishka', decimals: 18, isNative: false },
  { id: 'PEKPEK-PULSECHAIN', address: '0x9e63A3ECdCbe529140f8fF57114745381EFc9548', chainId: 369, name: 'IYKYK', symbol: '$PekPek', icon: 'pekpek', decimals: 18, isNative: false },
  { id: 'R92-PULSECHAIN', address: '0x2081B316d4920320B2C73Cfb692b13Da17aF891a', chainId: 369, name: 'Remember September 2nd', symbol: 'R92', icon: 'r92', decimals: 18, isNative: false },
  { id: '2MW-PULSECHAIN', address: '0x46085e1173c8FDf4B26Ab01464ad29D37AAFBD63', chainId: 369, name: '2MOREWEEKS', symbol: '2MW', icon: '2mw', decimals: 18, isNative: false },
  { id: 'ROME-PULSECHAIN', address: '0xd9Ce351E113A67B018b53F354c6fb92556B8c6CA', chainId: 369, name: 'ROME', symbol: 'ROME', icon: 'rome', decimals: 18, isNative: false },
  { id: 'GROYPER-PULSECHAIN', address: '0xdab88cD59f02BE6C82E49d5F9195602b52356c6d', chainId: 369, name: 'groyper', symbol: 'GROYPER', icon: 'groyper', decimals: 18, isNative: false },
  { id: 'OINK-PULSECHAIN', address: '0xFAaC6a85C3e123AB2CF7669B1024f146cFef0b38', chainId: 369, name: 'Piggy Bank', symbol: 'OINK', icon: 'oink', decimals: 18, isNative: false },
  { id: 'DEGEN-PULSECHAIN', address: '0x3E79130ab714E97ee73f86a56a2427bb1A519896', chainId: 369, name: 'DegenStrategy', symbol: 'DEGEN', icon: 'unknown', decimals: 18, isNative: false },
  { id: 'RAGE-PULSECHAIN', address: '0x0c6861A673A10C272bEa7947De5E60ab44B6BAc6', chainId: 369, name: 'Magikarp コイキング', symbol: 'RAGE', icon: 'rage', decimals: 18, isNative: false },
  { id: 'BASED-PULSECHAIN', address: '0x6F21950028418e0dFA840255daA1812200f7AB07', chainId: 369, name: 'Dev is Based', symbol: 'BASED', icon: 'based', decimals: 18, isNative: false },
  { id: 'RULER-PULSECHAIN', address: '0x8b615cB592f7c2addAFBFaf4A3fEf5E2323b88D7', chainId: 369, name: 'Size Matters', symbol: 'RULER', icon: 'ruler', decimals: 18, isNative: false },
  { id: 'WGPP-PULSECHAIN', address: '0x770CFA2FB975E7bCAEDDe234D92c3858C517Adca', chainId: 369, name: 'WORLDS GREATEST PDAI PRINTER', symbol: '🖨️', icon: 'wgpp', decimals: 18, isNative: false },
  { id: 'FUDEV-PULSECHAIN', address: '0x6002474F1e76bf7fE2AC3092313835A6AfbBda29', chainId: 369, name: 'Fuck You Brokie Devs', symbol: 'FUDEV', icon: 'fudev', decimals: 18, isNative: false },
  { id: 'FBI-PULSECHAIN', address: '0xC9536262dB226576e21693604C30f1b97aD5E32e', chainId: 369, name: 'FBI', symbol: 'FBI', icon: 'fbi', decimals: 18, isNative: false },
  { id: 'LIBERTY-PULSECHAIN', address: '0xe17A9786e62150c323c6c015F9844C581c409873', chainId: 369, name: 'Lord Liberty', symbol: 'LIBERTY', icon: 'liberty', decimals: 18, isNative: false },
  { id: 'PEL-PULSECHAIN', address: '0xeF159B1745D6946905e0300d10620ADF70D8ca66', chainId: 369, name: 'Pelican', symbol: 'PEL', icon: 'unknown', decimals: 18, isNative: false },
  { id: 'NERLIB-PULSECHAIN', address: '0x7F742ef92970E39ec3428712454041429F9DeA2C', chainId: 369, name: 'NEREUS LIBERTY', symbol: 'NERLIB', icon: 'nerlib', decimals: 18, isNative: false },
  { id: 'PORT-PULSECHAIN', address: '0x3F18345217a80E65339027c50363F36F757fd7c8', chainId: 369, name: 'portfolio.tires', symbol: 'PORT', icon: 'port', decimals: 18, isNative: false },
  { id: 'TRENCHES-PULSECHAIN', address: '0x9f228c6d0ddf98c02242815895a8b14e8b648d11', chainId: 369, name: 'Pulse Trenches', symbol: 'TRENCHES', icon: 'unknown', decimals: 18, isNative: false },
  { id: 'MOG-PULSECHAIN', address: '0x495d9b70480a22a82d0fb81981480764ba55550e', chainId: 369, name: 'Mog Coin', symbol: 'MOG', icon: 'mog', decimals: 18, isNative: false },
  { id: 'PLSD-PULSECHAIN', address: '0x34F0915a5f15a66Eba86F6a58bE1A471FB7836A7', chainId: 369, name: 'PulseDogecoin', symbol: 'PLSD', icon: 'plsd', decimals: 12, isNative: false },
  { id: 'SER-PULSECHAIN', address: '0x4C65176549d7F416F1C7E4ebac32517e21849553', chainId: 369, name: 'SER', symbol: 'SER', icon: 'ser', decimals: 18, isNative: false },
  { id: 'WLFI-PULSECHAIN', address: '0x4de992E0C1f6e1d68E49b797Af370A6be111B87f', chainId: 369, name: 'WorldLibertyFries', symbol: 'WLFI', icon: 'wlfi', decimals: 18, isNative: false },
  { id: 'EVDR-PULSECHAIN', address: '0x043860275e792025de09353F4Fa5b789B40e3539', chainId: 369, name: 'Tax evader', symbol: 'EVDR', icon: 'evdr', decimals: 18, isNative: false },
  { id: 'XGAME-PULSECHAIN', address: '0x4Eb7C1c05087f98Ae617d006F48914eE73fF8D2A', chainId: 369, name: 'XGAME.IO', symbol: 'XGAME', icon: 'xgame', decimals: 18, isNative: false },
  { id: 'SUNDAI-PULSECHAIN', address: '0x41C6b24019Bd67CC58fe7bb059D532C12356712B', chainId: 369, name: 'SUNDAI', symbol: 'SUNDAI', icon: 'sundai', decimals: 18, isNative: false },
  { id: '$TABLET-PULSECHAIN', address: '0xD5896371c92511eDC5EF004b229Cf7680227FAcF', chainId: 369, name: 'PulseChain Tablet', symbol: '$TABLET', icon: 'tablet2', decimals: 18, isNative: false },
  { id: 'CURV-PULSECHAIN', address: '0x591c914BC5c878cC398AF67681C388FaC8bdaf4e', chainId: 369, name: 'CURV', symbol: 'CURV', icon: 'curv', decimals: 18, isNative: false },
  { id: 'DAITRYING-PULSECHAIN', address: '0xcDe0D083d731115A21F17C99DFC949884125E46C', chainId: 369, name: 'DAITRYING', symbol: '$DaiTrying', icon: 'daitrying', decimals: 18, isNative: false },
  { id: 'PHEN-PULSECHAIN', address: '0xFDe3255Fb043eA55F9D8635C5e7FF18770A6a810', chainId: 369, name: 'PulseChain Peahen', symbol: 'PHEN', icon: 'phen', decimals: 18, isNative: false },
  { id: '?ETH-PULSECHAIN', address: '0xbDcab5789B5133016426508FA20D1E56623232bb', chainId: 369, name: 'Where ETH', symbol: '?ETH', icon: 'unknown', decimals: 18, isNative: false },
  { id: 'MAPS-PULSECHAIN', address: '0x0377aA8A0a67DD30270B9e890b218DFfd57f5366', chainId: 369, name: 'PulseChain Maps', symbol: 'MAPS', icon: 'maps', decimals: 18, isNative: false },
  { id: 'DSCVR-PULSECHAIN', address: '0x7AC30f7e5c9523716A22B05Fe58b9De8B3C73960', chainId: 369, name: 'DSCVR Crypto', symbol: 'DSCVR', icon: 'dscvr', decimals: 18, isNative: false },
  { id: 'SMILEX-PULSECHAIN', address: '0xecFc4b047659B5D9875D1e3243fe67400E0E47de', chainId: 369, name: 'SmileyHEX', symbol: 'SMILEX', icon: 'smilex', decimals: 18, isNative: false },
  { id: 'DRDOGE-PULSECHAIN', address: '0x9d95DFBB1aBb8F57abc20FDE4F786BB1Db52ad71', chainId: 369, name: 'DrDoge', symbol: 'DRDOGE', icon: 'drdoge', decimals: 18, isNative: false },
  { id: 'VAULT-PULSECHAIN', address: '0xeB52ac4D25067185f75bab4BcbfBaFA28c876A22', chainId: 369, name: 'VAULT 369', symbol: 'VAULT', icon: 'vault', decimals: 18, isNative: false },
  { id: 'PEPERONI-PULSECHAIN', address: '0x468aA62D8Be6E10b23997C8a382a1DcDaa386E3e', chainId: 369, name: 'Peperoni', symbol: 'peperoni', icon: 'peperoni', decimals: 18, isNative: false },
  { id: 'GRIMACE-PULSECHAIN', address: '0x1109D54A43C17c3B3d7613b0E0D0e02c984153e1', chainId: 369, name: 'Grimace', symbol: 'Grimace', icon: 'grimace', decimals: 18, isNative: false },
  { id: 'ZELDA-PULSECHAIN', address: '0x01272a2B4B5A7918Bb4AAbD02f4A267329EDe345', chainId: 369, name: 'Tiger Crypto Dog', symbol: 'ZELDA', icon: 'zelda', decimals: 18, isNative: false },
  { id: 'PTIGER-PULSECHAIN', address: '0xC2ACde27428d292C4E8e5A4A38148d6b7A2215f5', chainId: 369, name: 'PulseChain Tiger', symbol: 'PTIGER', icon: 'ptiger', decimals: 18, isNative: false },
  { id: 'PROVEX-PULSECHAIN', address: '0x469eA2Bbe42DD815C53418f8E587F6d0e8B449e1', chainId: 369, name: 'PrivatePower', symbol: 'PumpProveX', icon: 'provex', decimals: 18, isNative: false },
  { id: 'MRJEET-PULSECHAIN', address: '0xC56E1fE3C90B7C7ea15753590CB61ef2011bD45D', chainId: 369, name: 'MrJeet', symbol: 'MrJeet', icon: 'mrjeet', decimals: 18, isNative: false },
  { id: 'YOINK-PULSECHAIN', address: '0xfc975B5Dee0Bf337030a2310D2b4545263694cd3', chainId: 369, name: 'Yoink', symbol: 'YOINK', icon: 'yoink', decimals: 18, isNative: false },
  { id: 'PF-PULSECHAIN', address: '0x45794f23A8e8Fa4aBA05c60EaB9d0565b1d4845e', chainId: 369, name: 'Pulse Future', symbol: 'PF', icon: 'pf', decimals: 18, isNative: false },
  { id: 'ANON-PULSECHAIN', address: '0x2a27453e460aAa2f19BFBA356547c2CeaB123A1e', chainId: 369, name: 'PulseChain Anonymous', symbol: 'ANON', icon: 'anon', decimals: 18, isNative: false },
  { id: 'TROLL-PULSECHAIN', address: '0xCF2E8fC8EeFCc81a52468EcD081140a563A9c10A', chainId: 369, name: 'PLS TROLL', symbol: '$TROLL', icon: 'troll', decimals: 18, isNative: false },
  { id: 'WOULD-PULSECHAIN', address: '0x60B8deb28B1d6dD4eb829aeBAe8C8424ebbb47E8', chainId: 369, name: 'LIBERTITS', symbol: 'WOULD', icon: 'unknown', decimals: 18, isNative: false },
  { id: 'NEX-PULSECHAIN', address: '0xa0152f4BD207931797F2ec1BE4b5feE0E49Dc60a', chainId: 369, name: 'Nexus', symbol: 'Nex', icon: 'nex', decimals: 18, isNative: false },
  { id: 'KABUTO-PULSECHAIN', address: '0x82aeddc024fcf8bac56db4a245aeb0a9e71cc520', chainId: 369, name: 'Kabuto', symbol: 'KABUTO', icon: 'kabuto', decimals: 18, isNative: false },
  { id: 'PPAIN-PULSECHAIN', address: '0x0A32BA00830C48b9237D04bcF0794E24F88e4b6e', chainId: 369, name: 'PulsePain', symbol: 'PPAIN', icon: 'ppain', decimals: 18, isNative: false },
  { id: 'PONZI-PULSECHAIN', address: '0xD98a4B3F67D5300F2104209d260A28C08920dA25', chainId: 369, name: 'Just a Ponzi Guy', symbol: 'PONZI', icon: 'ponzi', decimals: 18, isNative: false },
  { id: 'MORBIUS-PULSECHAIN', address: '0xB7d4eB5fDfE3d4d3B5C16a44A49948c6EC77c6F1', chainId: 369, name: 'Morbius', symbol: 'Morbius', icon: 'morbius', decimals: 18, isNative: false },
  { id: 'DTGC-PULSECHAIN', address: '0xD0676B28a457371D58d47E5247b439114e40Eb0F', chainId: 369, name: 'Dump Tires Gold Coin', symbol: 'DTGC', icon: 'dtgc', decimals: 18, isNative: false },
  { id: 'REPLY-PULSECHAIN', address: '0xDB15aEa415095b53f11D2a7aCD8A7F4c0352fbfa', chainId: 369, name: 'Just a Reply Guy', symbol: 'REPLY', icon: 'reply', decimals: 18, isNative: false },
  { id: 'TOUCAN-PULSECHAIN', address: '0x0ED3A7ea3f008FB2827F0A62Fc72FbDF2F4a8Ae5', chainId: 369, name: 'Toucan', symbol: 'TOUCAN', icon: 'toucan', decimals: 18, isNative: false },
  { id: 'BURRITO-PULSECHAIN', address: '0x50062550Cc3481c08Ff8EAA84B59f5297fDcF01E', chainId: 369, name: 'Burrito', symbol: 'BURRITO', icon: 'burrito', decimals: 18, isNative: false },
  { id: 'DHEART-PULSECHAIN', address: '0x1A7A8f688f31d196F0482EcB38687bE915ECEAa4', chainId: 369, name: 'Diamond Heart', symbol: 'DHEART', icon: 'dheart', decimals: 18, isNative: false },
  { id: 'LEMON-PULSECHAIN', address: '0xDe090eafBda7Fa7A5166b6814f7c7214dB8E0DCB', chainId: 369, name: 'LEMON PARTY', symbol: 'LEMON', icon: 'lemon', decimals: 18, isNative: false },
  { id: '$KETCH-PULSECHAIN', address: '0x6b82EeBDd5CEA1a98ff90837616A83c10839AC26', chainId: 369, name: 'KETCH Coin', symbol: '$KETCH', icon: 'ketch', decimals: 18, isNative: false },
  { id: 'OFFICER-PULSECHAIN', address: '0x1971EDdD28aff499F721806C76a5A99b2f231884', chainId: 369, name: 'Bridge Officer', symbol: 'OFFICER', icon: 'officer', decimals: 18, isNative: false },
  { id: 'HERON-PULSECHAIN', address: '0x00C334951c36ca2E5c8cBE3270FBA358bd959b75', chainId: 369, name: 'Heron', symbol: 'HERON', icon: 'heron', decimals: 18, isNative: false },
  { id: 'FUPA-PULSECHAIN', address: '0xB41cEb0d0316e6c330b2Dd982779a4030C8FeDeB', chainId: 369, name: 'Fat Unsececured Ponzi Assets', symbol: 'FUPA', icon: 'fupa', decimals: 18, isNative: false },
  { id: 'DADDY-PULSECHAIN', address: '0xe7C6b52e6e7e463FEF7640580D1C586D0622D5d1', chainId: 369, name: 'SAC DADDY', symbol: 'DADDY', icon: 'daddy', decimals: 18, isNative: false },
  { id: 'MILADY-PULSECHAIN', address: '0xD100ecF770dC06D407787c3a991086a6E876Cc17', chainId: 369, name: 'Milady', symbol: 'MILADY', icon: 'milady', decimals: 18, isNative: false },
  { id: 'LOVE-PULSECHAIN', address: '0xb55EE890426341FE45EE6dc788D2D93d25B59063', chainId: 369, name: 'Love.io', symbol: 'LOVE', icon: 'love', decimals: 18, isNative: false },
  { id: 'RICHI-PULSECHAIN', address: '0x32608c7710ef58028869e9c4295dacb661bd7548', chainId: 369, name: 'Richius Maximus', symbol: 'RICHI', icon: 'richi', decimals: 18, isNative: false },
  { id: 'DARK-PULSECHAIN', address: '0xf40a744B60e15FeBfa63189216212F9a35f5a420', chainId: 369, name: 'Dark', symbol: 'DARK', icon: 'dark', decimals: 18, isNative: false },
  { id: 'STETH-PULSECHAIN', address: '0xae7ab96520de3a18e5e111b5eaab095312d7fe84', chainId: 369, name: 'Liquid staked Ether 2.0', symbol: 'STETH', icon: 'steth', decimals: 18, isNative: false },
  { id: 'FSJAL-PULSECHAIN', address: '0xae8b25B28D3eF3ADe67aD40Fd96af45faad3a271', chainId: 369, name: 'FSJAL', symbol: 'FSJAL', icon: 'fsjal', decimals: 18, isNative: false },
  { id: 'REKT-PULSECHAIN', address: '0x869a47f3ec495a73df118a46cdd2ebf97599af3e', chainId: 369, name: 'Rekt', symbol: 'REKT', icon: 'rekt', decimals: 18, isNative: false },
  { id: 'PITTBULL-PULSECHAIN', address: '0xf656637cEF4ad2Eac3c405Cc52c6b9776BFaF575', chainId: 369, name: 'Pittbull', symbol: 'Pittbull', icon: 'pittbull', decimals: 18, isNative: false },
  { id: 'DON-PULSECHAIN', address: '0xf0E6D8B1E79Da8a5AC0ecFD1343d90B53C6731AE', chainId: 369, name: 'The Pod Father', symbol: 'DON', icon: 'don', decimals: 18, isNative: false },
  { id: 'SCPYM-PULSECHAIN', address: '0x2248b73871eb69A0C1a6380BEFfFA02073bF192D', chainId: 369, name: 'SpicyPepper Mafia', symbol: 'SCPYM', icon: 'scpym', decimals: 18, isNative: false },
  { id: 'GIZMO-PULSECHAIN', address: '0x89f5d1c22394B4761F3bF746b60d54De03b59Ec6', chainId: 369, name: 'Gizmo', symbol: 'Gizmo', icon: 'gizmo', decimals: 18, isNative: false },
  { id: 'GAY-PULSECHAIN', address: '0x5D79A110A7eA6B369545dE5C602f6A78bf8AebE0', chainId: 369, name: 'AlmightyGay', symbol: 'GAY', icon: 'gay', decimals: 18, isNative: false },
  { id: 'TEDI-PULSECHAIN', address: '0x24e130bb9c0436FBB840dbE391861bEA45B96991', chainId: 369, name: 'Golden Bear', symbol: 'TEDI', icon: 'tedi', decimals: 18, isNative: false },
  { id: 'PHORSE-PULSECHAIN', address: '0xc75c9C2751e9F2B92aD09E9F42Bf20C57219cA78', chainId: 369, name: 'Phorse', symbol: 'pHORSE', icon: 'phorse', decimals: 18, isNative: false },
  { id: 'PRVX-PULSECHAIN', address: '0xF6f8Db0aBa00007681F8fAF16A0FDa1c9B030b11', chainId: 369, name: 'ProveX', symbol: 'PRVX', icon: 'prvx', decimals: 18, isNative: false },
  { id: 'BULLX-PULSECHAIN', address: '0x35B5F0Bd6264FfE48a848809Bb44583ed25CDd18', chainId: 369, name: 'Provex Bull', symbol: 'BULLX', icon: 'bullx', decimals: 18, isNative: false },
  { id: 'CACTUS-PULSECHAIN', address: '0x6394Bb15D18aEaA33c3B296bCa78A3243421229b', chainId: 369, name: 'Cactus', symbol: 'CACTUS', icon: 'unknown', decimals: 18, isNative: false },
  { id: 'GASMONEY-PULSECHAIN', address: '0x6dD29Cc8F8a2502421900E27bBdef8378BFA3281', chainId: 369, name: 'GAS Money', symbol: '⛽', icon: 'gasmoney', decimals: 18, isNative: false },
  { id: 'DOMINANCE-PULSECHAIN', address: '0x116D162d729E27E2E1D6478F1d2A8AEd9C7a2beA', chainId: 369, name: 'Dominance', symbol: 'DOMINANCE', icon: 'dominance', decimals: 18, isNative: false },
  { id: 'WTAO-PULSECHAIN', address: '0x44b9690aF9843CAf13B5a95f2BA0C8FA1F4be696', chainId: 369, name: 'WTAO', symbol: 'WTAO', icon: 'wtao', decimals: 18, isNative: false },
  { id: 'PAI-PULSECHAIN', address: '0xc40c58afFf0A8908c392a752F4951EfF381AA0b2', chainId: 369, name: 'Pulse Dai', symbol: 'PAI', icon: 'pai', decimals: 18, isNative: false },
  { id: 'HOLE-PULSECHAIN', address: '0x5614A6C8A34849e91Ffe6cA570C05f54C1422b98', chainId: 369, name: '$BONNIE\'s Giant Rusty Hole', symbol: 'HOLE', icon: 'hole', decimals: 18, isNative: false },
  { id: 'GLASS-PULSECHAIN', address: '0xd687FF2C9C5294f4A2bC0300B46eA921dB312063', chainId: 369, name: 'Glass', symbol: 'GLASS', icon: 'glass', decimals: 18, isNative: false },
  { id: 'PIKACHU-PULSECHAIN', address: '0x960b236A07cf122663c4303350609A66A7B288C0', chainId: 369, name: 'Pikachu', symbol: 'PIKACHU', icon: 'pikachu', decimals: 18, isNative: false },
  { id: 'SAC-PULSECHAIN', address: '0xA8D8e97C38F57A9D5Bf439E2403C64d2e67DFDC1', chainId: 369, name: 'Count Sacula', symbol: '$SAC', icon: 'sac', decimals: 18, isNative: false },
  { id: 'FLORK-PULSECHAIN', address: '0x7d1164e124a8109d27aac0b5508055c32c23162f', chainId: 369, name: 'Flork', symbol: 'FLORK', icon: 'flork', decimals: 18, isNative: false },
  { id: 'PMINT-PULSECHAIN', address: '0xFf640cBd35A618Df1348D861B5e47f7eaB05b422', chainId: 369, name: 'PMINT', symbol: 'pMINT', icon: 'pmint', decimals: 18, isNative: false },

]

/**
 * All tokens (bridgeable + DEX-only) combined for lookups
 */
// All tokens: bridgeable (from allTokens.ts) + DEX-only — used for lookups
const ALL_TOKENS = [
  ...LIBERTY_ALL_TOKENS,
  // `LIBERTY_ALL_TOKENS` was long a superset of `LIBERTY_TOKENS`, so merging the
  // bridge list looked redundant — until tokens were added to `LIBERTY_TOKENS`
  // only (ETH-BASE, ETH-ROBINHOOD, USDG-ROBINHOOD, USDC-MONAD). `tokenById()`
  // reads ALL_TOKENS, so those ids resolved to `undefined` and
  // `getLibertyInputTokens()` silently dropped them from the swap from-picker:
  // the tokens existed in the registry and had live routers, but no user could
  // select them. Merging the bridge list here keeps the two from drifting again.
  ...LIBERTY_TOKENS.filter(
    (bridge) =>
      !LIBERTY_ALL_TOKENS.some(
        (t) =>
          t.address.toLowerCase() === bridge.address.toLowerCase() && t.chainId === bridge.chainId
      )
  ),
  ...CCTP_TOKENS.filter(
    (cctp) =>
      !LIBERTY_ALL_TOKENS.some(
        (t) => t.address.toLowerCase() === cctp.address.toLowerCase() && t.chainId === cctp.chainId
      )
  ),
  ...CCTP_OUTPUT_ONLY_TOKENS.filter(
    (out) =>
      !LIBERTY_ALL_TOKENS.some(
        (t) => t.address.toLowerCase() === out.address.toLowerCase() && t.chainId === out.chainId
      )
  ),
  ...LIBERTY_DEX_TOKENS.filter(
    (dex) =>
      !LIBERTY_ALL_TOKENS.some(
        (t) => t.address.toLowerCase() === dex.address.toLowerCase() && t.chainId === dex.chainId
      )
  )
]

/**
 * Maps source bridge token IDs to their PulseChain equivalent address for DEX quoting.
 * Used to simulate: "if this token were bridged to PulseChain, what address would it have?"
 */
export const PULSECHAIN_INTERMEDIATE_TOKENS: Record<string, { address: string; decimals: number }> =
  {
    'USDC-ETH': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-BSC': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-BASE': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-ARBITRUM': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-POLYGON': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    // CCTP USDC bridges to PulseChain USDC (Circle mint), then DEX-swaps if the
    // destination is a non-USDC PulseChain token.
    'USDC-OPTIMISM': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-UNICHAIN': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-INK': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-MONAD': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDC-AVALANCHE': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    // USDG has no PulseChain counterpart; like USD1-BSC below, the bridge settles
    // it as PulseChain USDC.
    'USDG-ROBINHOOD': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 },
    'USDT-ETHEREUM': { address: '0x0Cb6F5a34ad42ec934882A05265A7d5F59b51A2f', decimals: 6 },
    'USDT-BSC': { address: '0x0Cb6F5a34ad42ec934882A05265A7d5F59b51A2f', decimals: 6 },
    'DAI-ETHEREUM': { address: '0xefD766cCb38EaF1dfd701853BFCe31359239F305', decimals: 18 },
    'ETH-ETHEREUM': { address: '0x02DcdD04e3F455D838cd1249292C58f3B79e3C3C', decimals: 18 },
    // Native ETH on Base / Robinhood bridges to the same PulseChain ETH.
    'ETH-BASE': { address: '0x02DcdD04e3F455D838cd1249292C58f3B79e3C3C', decimals: 18 },
    'ETH-ROBINHOOD': { address: '0x02DcdD04e3F455D838cd1249292C58f3B79e3C3C', decimals: 18 },
    'PCOCK-ETHEREUM': { address: '0xc10a4ed9b4042222d69ff0b374eddd47ed90fc1f', decimals: 18 },
    'PCOCK-BSC': { address: '0xc10a4ed9b4042222d69ff0b374eddd47ed90fc1f', decimals: 18 },
    'WPLS-ETHEREUM': { address: '0xA1077a294dDE1B09bB078844df40758a5D0f9a27', decimals: 18 },
    'PDAI-ETHEREUM': { address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', decimals: 18 },
    // USD1 has no PulseChain counterpart — the bridge settles it as PulseChain
    // USDC (USD1 is in `STABLECOIN_SYMBOLS`, i.e. 1:1 with USDC/USDT/DAI, and
    // `SHIELD_OUTPUT_TOKEN_IDS['USD1-BSC']` is USDC too). Mapping it to its own
    // BSC address made every USD1 → non-stablecoin quote ask the DEX for
    // `0x8d0d…8b0d` on chainId 369, which does not exist there: the API replied
    // HTTP 400 "Piteas API error: Request failed" and the UI showed "Unable to
    // get DEX quote for USD1 → …". The 18 → 6 decimal drop is applied by
    // `#calculateToAmountWithDecimals` in api.ts (same as USDT-BSC, 18 on BSC).
    'USD1-BSC': { address: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07', decimals: 6 }
  }

const PULSECHAIN_ID = 369

/**
 * Check if a token is DEX-only (no bridge contract, only reachable via bridge + DEX)
 */
export function isDexOnlyToken(tokenId: string): boolean {
  return LIBERTY_DEX_TOKENS.some((t) => t.id === tokenId)
}

/**
 * Check if a route requires bridge + DEX swap.
 * True when cross-chain AND the from/to tokens are different symbols
 * (e.g., USDT BSC → PLS PulseChain needs bridge USDT + DEX swap to PLS).
 * Same-symbol cross-chain (USDC → USDC) is a direct 1:1 bridge.
 */
/**
 * Stablecoin symbols that are treated as 1:1 equivalent for bridge purposes.
 * Bridge contract handles USDT↔USDC↔DAI conversions directly.
 *
 * NOTE: `pDAI` is intentionally NOT listed here. Despite the name, pDAI
 * (`0x6B17…1d0F` on PulseChain) is a pre-existing PulseChain-fork asset that
 * only trades on PulseX (~$0.0018), not a bridge-minted $1 stablecoin. Grouping
 * it with real stablecoins misclassified USDC/USDT/DAI → pDAI as a 1:1 direct
 * bridge (showing ~1:1 instead of the real ~1:552 DEX rate). A pDAI → pDAI
 * cross-chain move stays 1:1 via the `symbolA === symbolB` check below.
 *
 * USDG (Global Dollar, Robinhood Chain) IS listed: it settles 1:1 through
 * PulseChain USDC exactly like USD1-BSC, and its `PULSECHAIN_INTERMEDIATE_TOKENS`
 * entry IS PulseChain USDC. Leaving it out made `isCrossChainDexSwap` classify
 * USDG -> USDC as a bridge+DEX hop, so the DEX leg asked the quote API for
 * USDC -> USDC on chain 369 — a pair no DEX can quote. The throw landed AFTER
 * the user had already signed the EIP-2612 permit.
 */
const STABLECOIN_SYMBOLS = new Set(['USDC', 'USDT', 'DAI', 'USD1', 'USDG'])

/**
 * Is this token symbol one of the $1-pegged stablecoins we trade? Callers use it
 * to treat the token amount as its own USD figure — bridged stables (e.g.
 * PulseChain USDC) are quoted slightly off the peg by the price feeds, so their
 * fiat value is not a reliable stand-in for the amount the user typed.
 */
export function isStablecoinSymbol(symbol?: string): boolean {
  return STABLECOIN_SYMBOLS.has((symbol ?? '').toUpperCase())
}

/**
 * Wrapped native tokens treated as equivalent for bridge purposes.
 */
const WRAPPED_NATIVE_GROUPS: string[][] = [
  ['ETH', 'WETH'],
  ['PLS', 'WPLS']
]

export function areSameBridgeGroup(symbolA: string, symbolB: string): boolean {
  if (symbolA === symbolB) return true
  if (STABLECOIN_SYMBOLS.has(symbolA) && STABLECOIN_SYMBOLS.has(symbolB)) return true
  return WRAPPED_NATIVE_GROUPS.some((group) => group.includes(symbolA) && group.includes(symbolB))
}

/**
 * Check if this is a native wrap/unwrap (PLS↔WPLS, ETH↔WETH).
 * These should be handled as direct contract calls, not DEX swaps.
 */
export function isWrapUnwrap(
  fromChainId: number,
  toChainId: number,
  fromToken: LibertyTokenInfo,
  toToken: LibertyTokenInfo
): boolean {
  if (fromChainId !== toChainId) return false
  return WRAPPED_NATIVE_GROUPS.some(
    (group) => group.includes(fromToken.symbol) && group.includes(toToken.symbol)
  )
}

/**
 * Check if this is a same-chain PulseChain DEX swap (not a bridge).
 */
export function isSameChainDexSwap(
  fromChainId: number,
  toChainId: number,
  fromToken: LibertyTokenInfo,
  toToken: LibertyTokenInfo
): boolean {
  if (isWrapUnwrap(fromChainId, toChainId, fromToken, toToken)) return false
  return (
    fromChainId === toChainId &&
    fromChainId === PULSECHAIN_ID &&
    fromToken.symbol !== toToken.symbol
  )
}

export function isCrossChainDexSwap(
  fromChainId: number,
  toChainId: number,
  fromToken: LibertyTokenInfo,
  toToken: LibertyTokenInfo
): boolean {
  if (fromChainId === toChainId) return false
  // Same bridge group (stablecoins, wrapped natives) = direct bridge, no DEX
  if (areSameBridgeGroup(fromToken.symbol, toToken.symbol)) return false
  // Different asset type cross-chain = needs bridge + DEX for accurate quote
  return true
}

/** Token lookup by ID from ALL_TOKENS */
function tokenById(id: string): LibertyTokenInfo | undefined {
  return ALL_TOKENS.find((t) => t.id === id)
}

/**
 * Curated PulseChain receive-list order — mirrors liberty-swap
 * `PULSECHAIN_SUPPORTED_TOKENS` (apps/liberty-swap/src/constants/PulseChainSupported.ts).
 * The destination picker shows the same tokens in the same hand-ranked order as
 * the reference web app (popular assets first), instead of raw registry order.
 * Lookup is case-insensitive on the token id; tokens present in the registry but
 * absent from this list are appended after, preserving registry order (stable sort).
 */
const PULSECHAIN_DESTINATION_ORDER: string[] = [
  'USDC-PULSECHAIN', 'PLS-PULSECHAIN', 'ETH-PULSECHAIN', 'DAI-PULSECHAIN', 'PLSX-PULSECHAIN',
  'HEX-PULSECHAIN', 'INC-PULSECHAIN', 'CST-PULSECHAIN', 'USDL-PULSECHAIN', 'PXDC-PULSECHAIN',
  'PDAI-PULSECHAIN', 'PWBTC-PULSECHAIN', 'BEAR-PULSECHAIN', 'COM-PULSECHAIN', 'HDRN-PULSECHAIN',
  'SOLIDX-PULSECHAIN', 'AXIS-PULSECHAIN', 'ALIVE-PULSECHAIN', 'FIRE-PULSECHAIN', 'NOPE-PULSECHAIN',
  'PTS-PULSECHAIN', 'MOST-PULSECHAIN', 'PUMP-PULSECHAIN', 'PCOCK-PULSECHAIN', 'INTERN-PULSECHAIN',
  'INCD-PULSECHAIN', 'SOL-PULSECHAIN', 'CVRE-PULSECHAIN', 'UNSTABLE-PULSECHAIN', 'BRIAH-PULSECHAIN',
  'PEPE-PULSECHAIN', 'DWB-PULSECHAIN', 'URMOM-PULSECHAIN', 'JOHN-PULSECHAIN', 'TFC-PULSECHAIN',
  'DOUBT-PULSECHAIN', 'DOGE-PULSECHAIN', 'MEOWTH-PULSECHAIN', 'FRIENDSHIP-PULSECHAIN', 'NPC-PULSECHAIN',
  'TOPHAT-PULSECHAIN', 'SPX-PULSECHAIN', 'VOX-PULSECHAIN', 'GAINZ-PULSECHAIN', 'FART-PULSECHAIN',
  'YEET-PULSECHAIN', 'SEND-PULSECHAIN', 'PFLAN-PULSECHAIN', 'GASS-PULSECHAIN', 'UP-PULSECHAIN',
  'UPX-PULSECHAIN', 'CONRAD-PULSECHAIN', 'ATROPA-PULSECHAIN', 'VPLS-PULSECHAIN', 'WICK-PULSECHAIN',
  'PULSEGUY-PULSECHAIN', 'FLY-PULSECHAIN', 'HEXOS-PULSECHAIN', 'HELGO-PULSECHAIN', 'URKID-PULSECHAIN',
  'DUMP-PULSECHAIN', 'TJ-PULSECHAIN', 'DRAGON-PULSECHAIN', 'IM-PULSECHAIN', 'TIME-PULSECHAIN',
  'ISLAND-PULSECHAIN', 'DTO-PULSECHAIN', 'SYDE-PULSECHAIN', 'SS-PULSECHAIN', 'HOG-PULSECHAIN',
  'MORT-PULSECHAIN', 'MOONX-PULSECHAIN', 'BEEF-PULSECHAIN', 'PHEAD-PULSECHAIN', 'BIDET-PULSECHAIN',
  'X-PULSECHAIN', 'MEOW-PULSECHAIN', 'OOF-PULSECHAIN', 'CHOMP-PULSECHAIN', 'SHIB-PULSECHAIN',
  'ROB-PULSECHAIN', 'MCR369-PULSECHAIN', 'ZORP-PULSECHAIN', 'PBOSS-PULSECHAIN', 'GIGACOCK-PULSECHAIN',
  'PLSB-PULSECHAIN', 'BRIAN-PULSECHAIN', 'DBP-PULSECHAIN', 'FM-PULSECHAIN', 'PDONG-PULSECHAIN',
  'WILLOW-PULSECHAIN', 'STOMP-PULSECHAIN', 'BELLA-PULSECHAIN', 'MINICOCK-PULSECHAIN', 'PTRAIN-PULSECHAIN',
  'BAANA-PULSECHAIN', 'STM-PULSECHAIN', 'BKD-PULSECHAIN', 'GT-PULSECHAIN', 'PCOIN-PULSECHAIN',
  'ERECTION-PULSECHAIN', 'LBRTY-PULSECHAIN', 'XVR-PULSECHAIN', 'PEGGASAWASS-PULSECHAIN', '$REPOST-PULSECHAIN',
  'GECKY-PULSECHAIN', '2PHUX-PULSECHAIN', 'PRINT-PULSECHAIN', 'DEX-PULSECHAIN', 'XAVIER-PULSECHAIN',
  'PLAND-PULSECHAIN', 'CHARIZARD-PULSECHAIN', 'YEP-PULSECHAIN', 'FED-PULSECHAIN', 'TBILL-PULSECHAIN',
  'BFF-PULSECHAIN', 'GBABY-PULSECHAIN', 'AFFECTION-PULSECHAIN', 'EHEX-PULSECHAIN', 'PEGG-PULSECHAIN',
  'PULSECHAIN-PULSECHAIN', 'DEVC-PULSECHAIN', 'JAMES-PULSECHAIN', 'OPHIR-PULSECHAIN', 'ZEN-PULSECHAIN',
  'BOOMER-PULSECHAIN', 'BONNIE-PULSECHAIN', 'PTERN-PULSECHAIN', 'DORRA-PULSECHAIN', 'SHILL-PULSECHAIN',
  'ELON-PULSECHAIN', 'MARS-PULSECHAIN', 'TRUMP-PULSECHAIN', 'PUMPBITCOIN-PULSECHAIN', 'QUESO-PULSECHAIN',
  'LWH-PULSECHAIN', 'FLAPPY-PULSECHAIN', 'TOM-PULSECHAIN', 'PCLICKERDAO-PULSECHAIN', 'ZENC-PULSECHAIN',
  'THINK-PULSECHAIN', 'BITCOINHEX-PULSECHAIN', 'LOUIS-PULSECHAIN', 'ZKPULSE-PULSECHAIN', 'DREDD-PULSECHAIN',
  'PEGGY-PULSECHAIN', 'LSD-PULSECHAIN', 'POLLY-PULSECHAIN', 'KEEPER-PULSECHAIN', 'RICH-PULSECHAIN',
  'HERO-PULSECHAIN', 'ZERO-PULSECHAIN', 'POTTER-PULSECHAIN', 'VINE-PULSECHAIN', 'NOTHING-PULSECHAIN',
  'FEE-PULSECHAIN', 'COFFEE-PULSECHAIN', 'PV-PULSECHAIN', 'BULL-PULSECHAIN', 'ZONKERS-PULSECHAIN',
  'OGT+-PULSECHAIN', 'PIKAJEW-PULSECHAIN', 'PLSS-PULSECHAIN', 'TEDDY-PULSECHAIN', 'LIBERTYRUG-PULSECHAIN',
  'SWRM-PULSECHAIN', 'STOOPID-PULSECHAIN', 'DEVICE-PULSECHAIN', 'BAA-PULSECHAIN', 'PEPEWIFHAT-PULSECHAIN',
  'BX-PULSECHAIN', 'TABLET-PULSECHAIN', 'SIMP-PULSECHAIN', 'KISHKA-PULSECHAIN', 'PEKPEK-PULSECHAIN',
  'R92-PULSECHAIN', '2MW-PULSECHAIN', 'ROME-PULSECHAIN', 'GROYPER-PULSECHAIN', 'OINK-PULSECHAIN',
  'DEGEN-PULSECHAIN', 'RAGE-PULSECHAIN', 'BASED-PULSECHAIN', 'RULER-PULSECHAIN', 'WGPP-PULSECHAIN',
  'FUDEV-PULSECHAIN', 'FBI-PULSECHAIN', 'LIBERTY-PULSECHAIN', 'PEL-PULSECHAIN', 'NERLIB-PULSECHAIN',
  'PORT-PULSECHAIN', 'TRENCHES-PULSECHAIN', 'MOG-PULSECHAIN', 'PLSD-PULSECHAIN', 'SER-PULSECHAIN',
  'WLFI-PULSECHAIN', 'EVDR-PULSECHAIN', 'XGAME-PULSECHAIN', 'SUNDAI-PULSECHAIN', '$TABLET-PULSECHAIN',
  'CURV-PULSECHAIN', 'DAITRYING-PULSECHAIN', 'CRYFAGG-PULSECHAIN', 'PHEN-PULSECHAIN',
  'MAPS-PULSECHAIN', 'DSCVR-PULSECHAIN', 'SMILEX-PULSECHAIN', 'DRDOGE-PULSECHAIN', 'VAULT-PULSECHAIN',
  'PEPERONI-PULSECHAIN', 'GRIMACE-PULSECHAIN', 'ZELDA-PULSECHAIN', 'PTIGER-PULSECHAIN', 'PROVEX-PULSECHAIN',
  'MRJEET-PULSECHAIN', 'YOINK-PULSECHAIN', 'PF-PULSECHAIN', 'ANON-PULSECHAIN', 'TROLL-PULSECHAIN',
  'WOULD-PULSECHAIN', 'NEX-PULSECHAIN', 'KABUTO-PULSECHAIN', 'PPAIN-PULSECHAIN', 'PONZI-PULSECHAIN',
  'MORBIUS-PULSECHAIN', 'DTGC-PULSECHAIN', 'REPLY-PULSECHAIN', 'TOUCAN-PULSECHAIN', 'BURRITO-PULSECHAIN',
  'DHEART-PULSECHAIN', 'NIGGOLAS-PULSECHAIN', 'LEMON-PULSECHAIN', '$KETCH-PULSECHAIN', 'OFFICER-PULSECHAIN',
  'HERON-PULSECHAIN', 'FUPA-PULSECHAIN', 'DADDY-PULSECHAIN', 'MILADY-PULSECHAIN', 'LOVE-PULSECHAIN',
  'RICHI-PULSECHAIN', 'DARK-PULSECHAIN', 'STETH-PULSECHAIN', 'FSJAL-PULSECHAIN', 'REKT-PULSECHAIN',
  'PITTBULL-PULSECHAIN', 'DON-PULSECHAIN', 'SCPYM-PULSECHAIN', 'GIZMO-PULSECHAIN', 'GAY-PULSECHAIN',
  'TEDI-PULSECHAIN', 'PHORSE-PULSECHAIN', 'PRVX-PULSECHAIN', 'BULLX-PULSECHAIN', 'CACTUS-PULSECHAIN',
  'GASMONEY-PULSECHAIN', 'DOMINANCE-PULSECHAIN', 'WTAO-PULSECHAIN', 'PAI-PULSECHAIN', 'HOLE-PULSECHAIN',
  'GLASS-PULSECHAIN', 'PIKACHU-PULSECHAIN', 'RICHNIGGA-PULSECHAIN', 'SAC-PULSECHAIN', 'FLORK-PULSECHAIN',
  'PMINT-PULSECHAIN'
]

const PULSECHAIN_DESTINATION_RANK = new Map(
  PULSECHAIN_DESTINATION_ORDER.map((id, i) => [id.toLowerCase(), i])
)

/** All PulseChain tokens (for DEX swap destinations), in curated reference order. */
const PULSECHAIN_DEX_DESTINATION_TOKENS: LibertyTokenInfo[] = ALL_TOKENS.filter(
  (t) => t.chainId === PULSECHAIN_ID
).sort((a, b) => {
  const ra = PULSECHAIN_DESTINATION_RANK.get(a.id.toLowerCase()) ?? Number.MAX_SAFE_INTEGER
  const rb = PULSECHAIN_DESTINATION_RANK.get(b.id.toLowerCase()) ?? Number.MAX_SAFE_INTEGER
  return ra - rb // stable: equal ranks (both unranked) keep registry order
})

/**
 * Curated "🔥 Top tokens" quick-pick for the PulseChain receive list — mirrors
 * liberty-swap `OUTPUT_EXCHANGE_PULSECHAIN_TOP_TOKENS`. Resolved to full token
 * objects (missing ids dropped) for address-matching in the UI pill row.
 */
export const PULSECHAIN_TOP_TOKEN_IDS: string[] = [
  'USDC-PULSECHAIN',
  'PLS-PULSECHAIN',
  'ETH-PULSECHAIN',
  'PLSX-PULSECHAIN',
  'HEX-PULSECHAIN',
  'INC-PULSECHAIN',
  'PCOCK-PULSECHAIN',
  'PDAI-PULSECHAIN',
  'EHEX-PULSECHAIN',
  'PRVX-PULSECHAIN',
  'ZERO-PULSECHAIN'
]

export const PULSECHAIN_TOP_TOKENS: LibertyTokenInfo[] = PULSECHAIN_TOP_TOKEN_IDS.map((id) =>
  tokenById(id)
).filter((t): t is LibertyTokenInfo => !!t)

/**
 * Output token mapping per input token — sourced from LibertySwap web app.
 * Defines exactly which receive tokens are available for each send token.
 */
function getOutputTokenIds(inputTokenId: string): string[] | null {
  switch (inputTokenId) {
    case 'USDC-BASE':
    case 'USDC-ARBITRUM':
    case 'USDC-BSC':
    case 'USDC-ETH':
    case 'USDC-SOLANA':
    case 'USDC-OPTIMISM':
    case 'USDC-UNICHAIN':
    case 'USDC-INK':
    case 'USDC-POLYGON':
    case 'USDT-BSC':
    case 'USDT-ETH':
    case 'USDT-ETHEREUM':
    case 'USD1-BSC':
    case 'DAI-ETH':
    case 'DAI-ETHEREUM':
    case 'USDG-ROBINHOOD':
    case 'USDC-MONAD':
    case 'USDC-AVALANCHE':
      // → all PulseChain tokens (bridge + DEX)
      return null
    case 'ETH-ETH':
    case 'ETH-ETHEREUM':
    case 'ETH-BASE':
    case 'ETH-ROBINHOOD':
      // → all PulseChain tokens. liberty-swap's own
      // getOutputExchangeTokens('ETH-ETHEREUM') also prepends ETH-MEGAETH, but
      // MegaETH is deliberately unsupported here (see LIBERTY_INPUT_TOKEN_IDS).
      return null
    case 'USDC-PULSECHAIN':
      // Mirrors liberty getOutputExchangeTokens('USDC-PULSECHAIN'). Gasless EIP-2612
      // swap (relayer does Pulse→Base→CCTP burn/mint server-side), destination-agnostic
      // via outputToken{chainId,address} — see gasless.ts assembleGaslessSubmitRequest.
      // Arb/OP/Unichain/Ink resolve via allTokens.ts + CCTP_TOKENS; Linea/Morph/Plume/
      // World/Sei/Sonic/HyperEVM/Cronos/Pharos are receive-only
      // (CCTP_OUTPUT_ONLY_TOKENS). Only SOLANA is still skipped (needs a Solana
      // wallet — see docs/swap-cctp-out-plan.md §2).
      //
      // NOTE: liberty's own live bundle has since DROPPED Linea/Morph/Plume/World
      // from this list (checked 2026-08-26). They are kept here deliberately — this
      // change only adds the four Trello #445 destinations; removing existing ones
      // is a separate call that needs the relayer's confirmation that it no longer
      // mints there.
      return [
        'USDC-BASE',
        'USDC-BSC',
        'USDC-ETH',
        'USDC-POLYGON',
        'USDC-ARBITRUM',
        'USDC-OPTIMISM',
        'USDC-UNICHAIN',
        'USDC-INK',
        'USDC-AVALANCHE',
        // Receive-only CCTP destinations (CCTP_OUTPUT_ONLY_TOKENS) — relayer mints there.
        'USDC-LINEA',
        'USDC-MORPH',
        'USDC-PLUME',
        'USDC-WORLD',
        'USDC-SEI',
        'USDC-SONIC',
        'USDC-HYPEREVM',
        'USDC-CRONOS',
        'USDC-PHAROS'
      ]
    case 'PCOCK-PULSECHAIN':
      return ['PCOCK-ETHEREUM', 'PCOCK-BSC']
    case 'PCOCK-ETH':
    case 'PCOCK-ETHEREUM':
    case 'PCOCK-BSC':
      return ['PCOCK-PULSECHAIN']
    case 'PLS-PULSECHAIN':
      return ['WPLS-ETHEREUM']
    case 'WPLS-ETH':
    case 'WPLS-ETHEREUM':
      return ['PLS-PULSECHAIN']
    case 'ETH-PULSECHAIN':
      return ['ETH-ETHEREUM']
    case 'PDAI-ETH':
    case 'PDAI-ETHEREUM':
      return ['PDAI-PULSECHAIN']
    case 'PDAI-PULSECHAIN':
      return ['PDAI-ETHEREUM']
    default:
      return []
  }
}

/**
 * Gasless USDC-PULSECHAIN destinations whose fee is covered by the relayer's CCTP
 * burn/mint. Mirrors liberty SwapStore.isOutputCCTP set (CODEX/EDGE omitted — not
 * supported here). Used only to surface a cosmetic "CCTP fee: Sponsored by LibertySwap"
 * row; it does NOT change the swap path (still a normal gasless EIP-2612 swap).
 */
export const CCTP_OUTPUT_DESTINATION_IDS = new Set<string>([
  'USDC-ARBITRUM',
  'USDC-OPTIMISM',
  'USDC-UNICHAIN',
  'USDC-INK',
  'USDC-LINEA',
  'USDC-MORPH',
  'USDC-PLUME',
  'USDC-WORLD',
  'USDC-AVALANCHE',
  'USDC-SEI',
  'USDC-SOLANA',
  'USDC-SONIC',
  'USDC-HYPEREVM',
  'USDC-CRONOS',
  'USDC-PHAROS'
])

/** True when a gasless swap's CCTP bridging fee is sponsored by LibertySwap. */
export function isCctpSponsoredOutput(
  fromTokenId: string | null | undefined,
  toTokenId: string | null | undefined
): boolean {
  return fromTokenId === 'USDC-PULSECHAIN' && !!toTokenId && CCTP_OUTPUT_DESTINATION_IDS.has(toTokenId)
}

/**
 * Swap *input* (source) tokens, mirroring liberty INPUT_EXCHANGE_TOKENS
 * (supportedTokens.ts) — minus USDC-SOLANA (needs a Solana wallet). The swap
 * from-token picker is fed this fixed list (balances merged from portfolio where
 * held), so a user can pick any supported source even at 0 balance, exactly like
 * liberty. Order mirrors liberty for a stable picker layout.
 *
 * ETH-MEGAETH is deliberately EXCLUDED: MegaETH (chainId 4326) is not a
 * predefined wallet network, so the source could never be estimated or
 * broadcast — it only ever surfaced as an unsupported row in the picker.
 */
export const LIBERTY_INPUT_TOKEN_IDS: string[] = [
  'USDC-BASE',
  'USDC-ETH',
  'USDC-BSC',
  'USDC-ARBITRUM',
  'USDC-PULSECHAIN',
  'USDC-POLYGON',
  'USDC-OPTIMISM',
  'USDC-UNICHAIN',
  'USDC-INK',
  'USDC-AVALANCHE',
  'USDT-BSC',
  'USDT-ETHEREUM',
  'USD1-BSC',
  'DAI-ETHEREUM',
  'USDG-ROBINHOOD',
  'USDC-MONAD',
  'ETH-ETHEREUM',
  'ETH-BASE',
  'ETH-ROBINHOOD',
  'PCOCK-ETHEREUM',
  'PCOCK-BSC',
  'PCOCK-PULSECHAIN',
  'PLS-PULSECHAIN',
  'WPLS-ETHEREUM',
  'ETH-PULSECHAIN',
  'PDAI-ETHEREUM',
  'PDAI-PULSECHAIN'
]

/** Resolve a liberty token id (e.g. 'USDT-BSC') to its full token info, or undefined. */
export function getLibertyTokenById(id: string): LibertyTokenInfo | undefined {
  return tokenById(id)
}

/** Resolved swap input tokens (LIBERTY_INPUT_TOKEN_IDS), skipping any that fail to resolve. */
export function getLibertyInputTokens(): LibertyTokenInfo[] {
  return LIBERTY_INPUT_TOKEN_IDS.map((id) => tokenById(id)).filter(Boolean) as LibertyTokenInfo[]
}

/**
 * Input (source) tokens for the "swap from private RAILGUN balance" flow,
 * mirroring liberty's INPUT_RAILGUN_TOKENS (supportedTokens.ts). Unlike
 * LIBERTY_INPUT_TOKEN_IDS (public swap, 20+ tokens), LibertySwap only accepts
 * these 5 as the RAILGUN-side input — the "YOU SEND" picker in private mode
 * must be restricted to this set, not the full private wallet balance.
 */
export const RAILGUN_INPUT_TOKEN_IDS: string[] = [
  'USDC-ETH',
  'USDC-BSC',
  'USDC-ARBITRUM',
  'WETH-ETHEREUM',
  'WETH-BSC'
]

/** True if the given on-chain address+chainId is one of the RAILGUN_INPUT_TOKEN_IDS. */
export function isRailgunInputTokenSupported(address: string, chainId: number): boolean {
  const token = getLibertyTokenByAddressAndChain(address, chainId)
  return !!token && RAILGUN_INPUT_TOKEN_IDS.includes(token.id)
}

/**
 * Tokens LibertySwap can UNSHIELD (private RAILGUN balance -> public address),
 * mirroring liberty's INPUT_UNSHIELD_TOKENS (supportedTokens.ts). Kept as its own
 * constant (rather than reusing RAILGUN_INPUT_TOKEN_IDS) to track liberty's own
 * split — the two flows share the same set today but may diverge. The Unshield
 * "YOU SEND" picker must be restricted to this set; a private wallet can hold
 * RAILGUN tokens (e.g. WBNB, WETH-Arbitrum, native ETH-Arbitrum) that liberty
 * cannot unshield, and those must not appear as selectable.
 *
 * USDC-ARBITRUM was deliberately removed: liberty lists it, we don't offer it.
 */
export const INPUT_UNSHIELD_TOKEN_IDS: string[] = [
  'USDC-ETH',
  'USDC-BSC',
  'WETH-ETHEREUM',
  'WETH-BSC'
]

/** True if the given on-chain address+chainId is one of the INPUT_UNSHIELD_TOKEN_IDS. */
export function isUnshieldTokenSupported(address: string, chainId: number): boolean {
  const token = getLibertyTokenByAddressAndChain(address, chainId)
  return !!token && INPUT_UNSHIELD_TOKEN_IDS.includes(token.id)
}

/**
 * Shield *input* (public source) tokens, mirroring liberty-swap INPUT_SHIELD_TOKENS
 * (supportedTokens.ts). The Fast-shield "YOU SEND" picker is fed this fixed list
 * (portfolio balances merged in where held), so every supported source is
 * selectable even at 0 balance AND regardless of whether the wallet is viewing
 * public or private mode — exactly like liberty. Order mirrors liberty for a
 * stable picker layout.
 *
 * liberty also lists ETH-MEGAETH here; we deliberately don't. MegaETH (chainId
 * 4326) isn't a predefined wallet network, so the source was never actually
 * shieldable — it only ever rendered as an "add the network first" dead end.
 *
 * USDC-ARBITRUM / USDC-POLYGON are likewise deliberately absent: liberty lists
 * them as shield sources, we don't offer them. They stay in the gasless / CCTP /
 * swap whitelists — this list only governs the Shield "YOU SEND" picker.
 */
export const INPUT_SHIELD_TOKEN_IDS: string[] = [
  'USDC-PULSECHAIN',
  'USDC-BSC',
  'USDC-ETH',
  'USDC-BASE',
  'ETH-ETHEREUM',
  'ETH-PULSECHAIN',
  'USDT-ETHEREUM',
  'USDT-BSC',
  'DAI-ETHEREUM',
  'USD1-BSC'
]

/** Resolved shield input tokens (INPUT_SHIELD_TOKEN_IDS), skipping unresolved ids. */
export function getShieldInputTokens(): LibertyTokenInfo[] {
  return INPUT_SHIELD_TOKEN_IDS.map((id) => tokenById(id)).filter(
    (t): t is LibertyTokenInfo => !!t
  )
}

/** Resolve the liberty token id of a public shield input token (e.g. 'USDC-ETH'). */
export function getShieldInputTokenId(
  token: { address: string; chainId: bigint | number } | null
): string | null {
  if (!token) return null
  return getLibertyTokenByAddressAndChain(token.address, Number(token.chainId))?.id ?? null
}

/**
 * Valid RAILGUN output (private-balance) tokens per shield input token, mirroring
 * liberty-swap getOutputShieldTokens (supportedTokens.ts). Stablecoin inputs
 * shield into USDC on Ethereum / BNB; native-ETH / WETH inputs shield into WETH on
 * Ethereum / BNB. Every RAILGUN output settles on an Ethereum(1) / BNB(56) chain,
 * both RAILGUN-supported. Keys mirror INPUT_SHIELD_TOKEN_IDS — no entry for
 * USDC-ARBITRUM / USDC-POLYGON, which are not offered as shield sources.
 */
export const SHIELD_OUTPUT_TOKEN_IDS: Record<string, string[]> = {
  'USDC-PULSECHAIN': ['USDC-ETH', 'USDC-BSC'],
  'USDC-ETH': ['USDC-ETH', 'USDC-BSC'],
  'USDC-BASE': ['USDC-ETH', 'USDC-BSC'],
  'USDT-ETHEREUM': ['USDC-ETH', 'USDC-BSC'],
  'USDT-BSC': ['USDC-ETH', 'USDC-BSC'],
  'DAI-ETHEREUM': ['USDC-ETH', 'USDC-BSC'],
  'USD1-BSC': ['USDC-ETH', 'USDC-BSC'],
  'USDC-BSC': ['USDC-BSC'],
  'ETH-ETHEREUM': ['WETH-ETHEREUM'],
  'ETH-PULSECHAIN': ['WETH-ETHEREUM', 'WETH-BSC']
}

/**
 * RAILGUN output tokens a Fast shield can deliver for the given public input
 * token. Empty when the input isn't a supported Fast-shield source.
 */
export function getShieldOutputTokens(
  token: { address: string; chainId: bigint | number } | null
): LibertyTokenInfo[] {
  const inputId = getShieldInputTokenId(token)
  if (!inputId) return []
  const ids = SHIELD_OUTPUT_TOKEN_IDS[inputId] ?? []
  return ids.map((id) => tokenById(id)).filter((t): t is LibertyTokenInfo => !!t)
}

/**
 * The LibertySwap registry id to build a receive (to) token list from, for the
 * given send token. Resolved over the FULL registry rather than over the
 * bridge-router map, because a supported swap input does not necessarily have a
 * router: USDC on Ink/Optimism/Unichain moves over Circle CCTP instead, so the
 * router-filtered lookup returned `undefined` for it and the receive list came
 * back empty (which then cleared the user's receive token — Trello #373).
 *
 * Only ids that are actual swap inputs are accepted. A token that merely appears
 * in the registry (e.g. any PulseChain DEX token held in the portfolio) has no
 * `getOutputTokenIds` mapping, and returning its id would collapse its receive
 * list to `[]` instead of the "unrecognized PulseChain token → DEX destinations"
 * fallback in `getAvailableToTokens`.
 */
export function getLibertyFromTokenIdForToTokenList(
  token: { address?: string; chainId?: bigint | number | null } | null | undefined
): string | undefined {
  if (!token?.address || token.chainId === null || token.chainId === undefined) return undefined

  const id = getLibertyTokenByAddressAndChain(token.address, Number(token.chainId))?.id

  return id && LIBERTY_INPUT_TOKEN_IDS.includes(id) ? id : undefined
}

/**
 * Given a from-token, returns valid destination tokens.
 * Mapping sourced from LibertySwap web app (supportedTokens.ts).
 */
export function getAvailableToTokens(
  _fromChainId: number,
  _toChainId: number,
  _fromTokenSymbol?: string,
  fromTokenId?: string,
  fromChainIdNum?: number
): LibertyTokenInfo[] {
  if (!fromTokenId && !fromChainIdNum) return []

  // A SAME-CHAIN pair is a DEX swap (the Permit2 path), not a bridge, so the
  // bridge mapping below is the wrong question to ask: `getOutputTokenIds` maps a
  // recognized input to where it can BRIDGE OUT to, which for a PulseChain source
  // like `USDC-PULSECHAIN` is USDC on every OTHER chain — none of them on the
  // requested chain, so a 369 → 369 pair resolved to an empty receive list.
  //
  // Callers that don't know the destination pass `_toChainId: 0`, which is why
  // this only triggers on an explicit, equal destination chain. PulseChain is the
  // only chain LibertySwap runs a DEX on; a same-chain pair anywhere else belongs
  // to another provider (see `zkxTrade/gate.ts`), and claiming it here would race
  // two quotes onto one pair.
  const sourceChainId = fromChainIdNum ?? _fromChainId
  if (!!_toChainId && !!sourceChainId && _toChainId === sourceChainId) {
    return sourceChainId === PULSECHAIN_ID ? PULSECHAIN_DEX_DESTINATION_TOKENS : []
  }

  // A recognized LibertySwap token (has an explicit entry in the registry) is
  // governed strictly by getOutputTokenIds — including tokens that explicitly
  // have NO valid destination (default [] case). Do NOT fall back to "all
  // PulseChain tokens" for these — that would wrongly surface destinations
  // that liberty never supports for the token.
  if (fromTokenId) {
    const specificIds = getOutputTokenIds(fromTokenId)

    // Known bridge token → all PulseChain tokens (null = PulseChain destinations)
    if (specificIds === null) {
      return PULSECHAIN_DEX_DESTINATION_TOKENS
    }

    // Known bridge token → specific destinations only (possibly empty).
    // Mirrors liberty getOutputExchangeTokens: each mapped input returns exactly
    // its explicit destination list. Do NOT append all PulseChain DEX tokens for
    // PulseChain sources — that floods the receive list (e.g. USDC-PULSECHAIN must
    // only show USDC on other chains, not WPLS/PLS/MOST/PCOCK/COM...).
    return specificIds.map((id) => tokenById(id)).filter(Boolean) as LibertyTokenInfo[]
  }

  // Genuinely unrecognized token (no LibertySwap registry entry at all, e.g.
  // a random meme coin held in the portfolio like HEX/BEAR) → best-effort
  // assume it's DEX-tradable against other PulseChain tokens.
  if (fromChainIdNum === PULSECHAIN_ID) {
    return PULSECHAIN_DEX_DESTINATION_TOKENS
  }

  return []
}

/**
 * OFFLINE FALLBACK for the V1 contract set (the older ABI, without
 * `recipientType`), used only while `/app/routers` has not been loaded for a
 * token. The registry's own `version` field is the source of truth — see
 * `shouldUseContractV1`.
 *
 * Treat this list as a snapshot that goes stale: LibertySwap redeploys routers,
 * and a redeploy can flip a token from v1 to v2/v3 without the address list here
 * changing at all. As of 2026-09-03 the prod registry reports v3 for every entry
 * it serves, pDAI included (it was migrated off V1), so this snapshot only has to
 * carry the app until `/app/routers` answers. The live version is what decides.
 */
const V1_TOKEN_IDS = new Set<string>([
  'PCOCK-PULSECHAIN',
  'PCOCK-ETHEREUM',
  'PCOCK-BSC',
  'PLS-PULSECHAIN',
  // pDAI is no longer here: liberty migrated both pDAI routers from V1 to V3 on
  // the prod registry (checked 2026-09-03), and the addresses above now point at
  // those V3 routers, whose `swap` is the V2 selector (0x095ff22d).
  // WPLS-ETHEREUM router (0x05CB1107…) is a V1 contract. Omitting it made the
  // wallet encode V2 swap calldata (selector 0xed81cfdf) and send it to the V1
  // contract, which has no such selector → fell through to the fallback: the tx
  // "succeeds" with ~24k gas but never runs transferFrom, so no WPLS is bridged.
  // Ground truth: liberty-swap ProdContract.ts marks chainId 1 / WPLS as v1.
  'WPLS-ETHEREUM'
])

/**
 * Whether `tokenId`'s bridge router speaks the V1 swap ABI.
 *
 * Prefers the live `/app/routers` version, so a router redeployed from v1 to v2
 * (or back) is picked up without a code change — the wallet already takes the
 * address from that same response, and only the version tells the two ABIs apart.
 * Anything the registry reports that is not `"v1"` uses the V2 ABI, matching
 * liberty-swap's own `isContractV1(item) ? "v1" : "v2"`; that is deliberate and
 * verified for `"v3"`, whose routers expose the identical V2 `swap` selector.
 *
 * Falls back to the hardcoded snapshot only when the registry has not supplied a
 * version for this token (not loaded yet, or the token is absent from it).
 */
export function shouldUseContractV1(tokenId: string): boolean {
  const liveVersion = DYNAMIC_SWAP_VERSIONS?.[tokenId]
  if (liveVersion) return liveVersion.trim().toLowerCase() === 'v1'

  return V1_TOKEN_IDS.has(tokenId)
}
